/* ══════════════════════════════════════════════════════════════════════
   Animated diagrams for the Berlin Buzzwords 2026 broker-side-filtering
   deck. Two SVGs, both CSS-keyframe-animated:

     <ProblemDiagram />   — slide 7. "Every consumer pays for every key."
                            A multi-key stream flows from a Topic into a
                            single Consumer-of-K1. K1 tiles "land" inside
                            the consumer; the others arrive and dissolve
                            (representing client-side discard).

     <SolutionDiagram />  — slide 10. "Broker-side filter, three branches."
                            Same source firehose hits a central filter.
                            K3 tiles route to portfolio-Account-1, K1 tiles
                            to watchlist-Account-1, K2/K6 tiles to
                            watchlist-Account-2; other keys are absorbed
                            at the filter.

   Both diagrams use plain CSS keyframes embedded in a <style> block
   inside the SVG so they animate the moment the slide mounts and loop
   forever. No JS, no requestAnimationFrame, no React state.
   ══════════════════════════════════════════════════════════════════════ */

import { Fragment, useState, useEffect, useRef, type CSSProperties } from 'react';

/* ----------- Shared key palette ------------------------------------- */

type KeyClass = 'K0' | 'K1' | 'K2' | 'K3' | 'K4' | 'K5' | 'K6' | 'K7';

const KEY_FILL: Record<KeyClass, string> = {
  K0: '#BFC1C4',  // grey
  K1: '#0070D9',  // SN blue — the "highlighted" key on slide 7
  K2: '#0EC1B5',  // SN teal-ish
  K3: '#E89BB5',  // pink
  K4: '#F5C97A',  // amber
  K5: '#C89BD9',  // lavender
  K6: '#5B6770',  // slate
  K7: '#8FC9D3',  // teal-soft
};

const KEY_STROKE: Record<KeyClass, string> = {
  K0: '#9FA3A8',
  K1: '#0C5FB7',
  K2: '#0A8F86',
  K3: '#B86B85',
  K4: '#C29553',
  K5: '#8E6BA9',
  K6: '#2F3A45',
  K7: '#5E9099',
};

/* ══════════════════════════════════════════════════════════════════════
   ProblemDiagram — slide 7
   viewBox 1200×500. Tiles SNAKE through three lanes inside the Topic
   box (right → U-turn down → left → U-turn down → right), then exit
   the topic on the right and hit the Filter. The Filter carries an
   explicit "ACCEPTS K1 K3" legend. Matching tiles pass through the
   slot and continue along the pipe to the Consumer; everything else
   arcs down into the Discarded bin.

   Implementation note (don't drop the wrapper <g>): in SVG, a CSS
   `transform` property OVERRIDES a `transform` attribute on the same
   element — so the anchor MUST live on an outer <g> via the SVG
   attribute, and the CSS animation lives on a nested inner <g>. That
   way attribute + CSS compose instead of fight.
   ══════════════════════════════════════════════════════════════════════ */

// Which keys the filter accepts. Tiles with these keys take the
// pass-through path; everything else drops.
const PROBLEM_ACCEPTED: ReadonlyArray<KeyClass> = ['K1', 'K3'];

function isAccepted(k: KeyClass): boolean {
  return PROBLEM_ACCEPTED.includes(k);
}

// Each entry is one tile in the staggered stream. The visual rhythm of
// the snake reads best when ~25-30% of tiles match (audience reads
// "occasional passes, mostly drops" without the consumer looking idle).
const PROBLEM_TILES: ReadonlyArray<{ key: KeyClass; delay: number }> = [
  { key: 'K3', delay: 0.0 },   // MATCH
  { key: 'K4', delay: 0.5 },
  { key: 'K7', delay: 1.0 },
  { key: 'K1', delay: 1.5 },   // MATCH
  { key: 'K2', delay: 2.0 },
  { key: 'K0', delay: 2.5 },
  { key: 'K5', delay: 3.0 },
  { key: 'K6', delay: 3.5 },
  { key: 'K3', delay: 4.0 },   // MATCH
  { key: 'K2', delay: 4.5 },
  { key: 'K4', delay: 5.0 },
  { key: 'K1', delay: 5.5 },   // MATCH
  { key: 'K7', delay: 6.0 },
  { key: 'K0', delay: 6.5 },
];

const PROBLEM_CYCLE_SECONDS = 7;

// Tile anchor — left edge of lane 1 inside the topic. The outer <g>
// applies this via SVG transform attribute; inner <g> animates from
// (0, 0) in this local frame.
const PROBLEM_ANCHOR_X = 60;
const PROBLEM_ANCHOR_Y = 148;

const PROBLEM_CSS = `
  .problem-tile {
    animation-duration: ${PROBLEM_CYCLE_SECONDS}s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }
  .problem-tile-label { font-family: 'DM Mono', monospace; font-size: 14px; font-weight: 700; fill: white; }
  .problem-tile-match { animation-name: problem-flow-match; }
  .problem-tile-drop  { animation-name: problem-flow-drop; }

  /* Snake path inside the topic, then through filter slot to consumer.
     Lanes are 55px apart vertically; horizontal travel is 380px per lane. */
  @keyframes problem-flow-match {
    0%   { transform: translate(0px,    0px); opacity: 0; }
    4%   { transform: translate(15px,   0px); opacity: 1; }
    /* Lane 1 (top) — left → right */
    22%  { transform: translate(380px,  0px); opacity: 1; }
    /* U-turn down to lane 2 */
    28%  { transform: translate(380px,  55px); opacity: 1; }
    /* Lane 2 (middle) — right → left */
    50%  { transform: translate(0px,    55px); opacity: 1; }
    /* U-turn down to lane 3 */
    56%  { transform: translate(0px,   110px); opacity: 1; }
    /* Lane 3 (bottom) — left → right */
    78%  { transform: translate(380px, 110px); opacity: 1; }
    /* Exit topic, enter filter slot */
    84%  { transform: translate(484px, 110px); opacity: 1; }
    /* Exit filter slot */
    90%  { transform: translate(596px, 110px); opacity: 1; }
    /* Travel pipe to consumer */
    98%  { transform: translate(860px, 110px); opacity: 1; }
    100% { transform: translate(860px, 110px); opacity: 0; }
  }

  @keyframes problem-flow-drop {
    0%   { transform: translate(0px,    0px); opacity: 0; }
    4%   { transform: translate(15px,   0px); opacity: 1; }
    22%  { transform: translate(380px,  0px); opacity: 1; }
    28%  { transform: translate(380px,  55px); opacity: 1; }
    50%  { transform: translate(0px,    55px); opacity: 1; }
    56%  { transform: translate(0px,   110px); opacity: 1; }
    78%  { transform: translate(380px, 110px); opacity: 1; }
    /* Approach filter (but doesn't enter slot — solid wall above) */
    83%  { transform: translate(475px, 110px); opacity: 1; }
    /* Hit the filter wall, start falling */
    87%  { transform: translate(485px, 160px); opacity: 0.85; }
    /* Arc into the discard bin */
    93%  { transform: translate(530px, 230px); opacity: 0.55; filter: saturate(0.4); }
    98%  { transform: translate(580px, 260px); opacity: 0.3; filter: saturate(0); }
    100% { transform: translate(580px, 260px); opacity: 0; filter: saturate(0); }
  }

  .problem-consumer-ring {
    animation: problem-ring-pulse 1.2s ease-in-out infinite alternate;
  }
  @keyframes problem-ring-pulse {
    0%   { opacity: 0.45; }
    100% { opacity: 0.95; }
  }

  .problem-drop-arrow {
    stroke-dasharray: 4 4;
    animation: problem-drop-arrow-march 1.2s linear infinite;
  }
  @keyframes problem-drop-arrow-march {
    0%   { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: -16; }
  }
`;

function ProblemTile({ kClass, delay }: { kClass: KeyClass; delay: number }) {
  // Anchor lives on outer <g> via the SVG `transform` attribute.
  // CSS animation lives on inner <g> via the CSS `transform` property —
  // composes on top of the outer attribute instead of replacing it.
  const cls = isAccepted(kClass)
    ? 'problem-tile problem-tile-match'
    : 'problem-tile problem-tile-drop';
  const style: CSSProperties = { animationDelay: `${delay}s` };
  return (
    <g transform={`translate(${PROBLEM_ANCHOR_X} ${PROBLEM_ANCHOR_Y})`}>
      <g className={cls} style={style}>
        <rect width={52} height={36} rx={6} fill={KEY_FILL[kClass]} stroke={KEY_STROKE[kClass]} strokeWidth={1.5} />
        <text x={26} y={23} className="problem-tile-label" textAnchor="middle">{kClass}</text>
      </g>
    </g>
  );
}

export function ProblemDiagram() {
  return (
    <svg
      viewBox="0 0 1200 510"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Animated diagram: tiles snake through three lanes inside a Topic, then hit a Filter labeled 'Accepts: K1, K3'. Matching tiles pass through the filter slot to a Consumer; others arc down into a Discarded bin."
      style={{ width: '100%', height: 'auto', maxHeight: 560 }}
    >
      <style>{PROBLEM_CSS}</style>

      {/* ─── Topic box — tiles snake through this rectangle ────────────── */}
      <g>
        <rect x={40} y={120} width={460} height={220} rx={16} fill="#F4F2EE" stroke="#5B6770" strokeWidth={2} />
        <text x={270} y={108} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={22} fontWeight={700} fill="#202939">
          Topic
        </text>
        <text x={270} y={362} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fill="#5B6770" letterSpacing={1.5}>
          350 KEYS · CONTINUOUS
        </text>
      </g>

      {/* ─── The traveling tiles — drawn before the filter so the slot's
              dashed border reads ON TOP of any tile passing through. ──── */}
      {PROBLEM_TILES.map((t, i) => (
        <ProblemTile key={i} kClass={t.key} delay={t.delay} />
      ))}

      {/* ─── Filter element — between Topic and Consumer ──────────────── */}
      <g>
        {/* Outer filter frame */}
        <rect x={540} y={140} width={120} height={200} rx={12} fill="#FFFFFF" stroke="#0070D9" strokeWidth={2.5} />

        {/* "ACCEPTS" legend header */}
        <text x={600} y={158} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#0C5FB7" letterSpacing={1.8}>
          ACCEPTS
        </text>

        {/* K1 sample badge — first accepted key */}
        <rect x={552} y={170} width={42} height={32} rx={5} fill={KEY_FILL.K1} stroke={KEY_STROKE.K1} strokeWidth={1.5} />
        <text x={573} y={191} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={13} fontWeight={700} fill="white">K1</text>

        {/* K3 sample badge — second accepted key */}
        <rect x={606} y={170} width={42} height={32} rx={5} fill={KEY_FILL.K3} stroke={KEY_STROKE.K3} strokeWidth={1.5} />
        <text x={627} y={191} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={13} fontWeight={700} fill="white">K3</text>

        {/* Divider between legend and slot */}
        <line x1={552} y1={222} x2={648} y2={222} stroke="#D9D6D0" strokeWidth={1.5} />

        {/* "SLOT" caption */}
        <text x={600} y={246} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={700} fill="#5B6770" letterSpacing={1.5}>
          SLOT
        </text>

        {/* Filter slot — the cutout aligned with lane 3, where matching
            tiles pass through. Drawn after tiles so the dashed border
            visually frames the tile as it crosses. */}
        <rect x={544} y={258} width={112} height={36} rx={4} fill="none" stroke="#0070D9" strokeWidth={1.5} strokeDasharray="3 3" />

        {/* Filter label below the box */}
        <text x={600} y={362} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={16} fontWeight={700} fill="#202939">
          Filter
        </text>
      </g>

      {/* ─── Pipe from filter slot to consumer (the pass-through line) ── */}
      <line x1={660} y1={276} x2={920} y2={276} stroke="#9FA3A8" strokeWidth={2} strokeDasharray="4 4" />

      {/* ─── Consumer box on the right ────────────────────────────────── */}
      <g>
        <rect className="problem-consumer-ring" x={910} y={216} width={240} height={140} rx={14}
              fill="none" stroke="#0070D9" strokeWidth={3} strokeDasharray="6 4" />
        <rect x={920} y={226} width={220} height={120} rx={10} fill="#FFFFFF" stroke="#202939" strokeWidth={2} />
        <text x={1030} y={258} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={20} fontWeight={700} fill="#202939">
          Consumer
        </text>
        <text x={1030} y={282} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={15} fill="#0C5FB7">
          watching K1 · K3
        </text>
        <text x={1030} y={322} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#5B6770" letterSpacing={1.2}>
          ONLY THE MATCHES
        </text>
      </g>

      {/* ─── Drop path — animated dashed arrow from filter to bin ─────── */}
      <path className="problem-drop-arrow" d="M 580 340 Q 580 380 690 408" fill="none" stroke="#B86B85" strokeWidth={2.5} />
      <polygon points="690,408 680,400 680,416" fill="#B86B85" />

      {/* ─── Discarded bin (below the filter) ─────────────────────────── */}
      <g>
        <rect x={600} y={400} width={200} height={70} rx={10} fill="#FBE8EE" stroke="#B86B85" strokeWidth={1.5} strokeDasharray="3 3" />
        <text x={700} y={427} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#8B3F58" letterSpacing={1.5}>
          DISCARDED
        </text>
        <text x={700} y={450} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={13} fill="#8B3F58">
          everything the filter doesn&apos;t accept
        </text>
      </g>

      {/* ─── Caption ──────────────────────────────────────────────────── */}
      <text x={600} y={500} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={18} fill="#2F3A45">
        One filter. Keeps what matches. Drops what doesn&apos;t — at the broker, not at the client.
      </text>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   SolutionDiagram — slide 10
   viewBox 1200×720. Replicates the slide-7 snake-and-filter pattern but
   scaled up to three filters stacked vertically — one per consumer,
   each with its own ACCEPTS legend. The source topic streams keys
   through a 3-lane snake; tiles exit on the right and route to:
     - Filter 1 (ACCEPTS K3)     → portfolio · Account 1
     - Filter 2 (ACCEPTS K1)     → watchlist · Account 1
     - Filter 3 (ACCEPTS K2 K6)  → watchlist · Account 2
   Tiles that match none of the filters arc down into a single
   Discarded bin below the filter column.
   ══════════════════════════════════════════════════════════════════════ */

type BranchTarget = 'portfolio-A1' | 'watchlist-A1' | 'watchlist-A2' | 'drop';

function branchFor(k: KeyClass): BranchTarget {
  if (k === 'K3') return 'portfolio-A1';
  if (k === 'K1') return 'watchlist-A1';
  if (k === 'K2' || k === 'K6') return 'watchlist-A2';
  return 'drop';
}

// One config per filter row. y is the filter's outer top; slotY is the
// tile-top y for the slot/cutout (where a passing tile renders). The
// consumer box and the pipe both align with slotY + 18 (tile center).
type FilterRow = {
  target: Exclude<BranchTarget, 'drop'>;
  y: number;
  slotY: number;
  accepts: ReadonlyArray<KeyClass>;
  topicName: string;
  keysLabel: string;
};

const FILTER_ROWS: ReadonlyArray<FilterRow> = [
  { target: 'portfolio-A1', y: 140, slotY: 200, accepts: ['K3'],       topicName: 'portfolio · A1', keysLabel: 'K3' },
  { target: 'watchlist-A1', y: 290, slotY: 350, accepts: ['K1'],       topicName: 'watchlist · A1', keysLabel: 'K1' },
  { target: 'watchlist-A2', y: 440, slotY: 500, accepts: ['K2', 'K6'], topicName: 'watchlist · A2', keysLabel: 'K2 · K6' },
];

const FILTER_X = 540;
const FILTER_WIDTH = 140;
const FILTER_HEIGHT = 120;
const FILTER_SLOT_X = 544;
const FILTER_SLOT_WIDTH = 132;
const CONSUMER_X = 940;
const CONSUMER_WIDTH = 240;
const CONSUMER_HEIGHT = 100;
const DROP_BIN_X = 540;
const DROP_BIN_Y = 590;
const DROP_BIN_WIDTH = 200;
const DROP_BIN_HEIGHT = 80;

const SOLUTION_TILES: ReadonlyArray<{ key: KeyClass; delay: number }> = [
  { key: 'K3', delay: 0.0 },   // → Filter 1
  { key: 'K1', delay: 0.5 },   // → Filter 2
  { key: 'K4', delay: 1.0 },   // drop
  { key: 'K2', delay: 1.5 },   // → Filter 3
  { key: 'K7', delay: 2.0 },   // drop
  { key: 'K1', delay: 2.5 },   // → Filter 2
  { key: 'K0', delay: 3.0 },   // drop
  { key: 'K6', delay: 3.5 },   // → Filter 3
  { key: 'K3', delay: 4.0 },   // → Filter 1
  { key: 'K5', delay: 4.5 },   // drop
  { key: 'K1', delay: 5.0 },   // → Filter 2
  { key: 'K2', delay: 5.5 },   // → Filter 3
  { key: 'K3', delay: 6.0 },   // → Filter 1
  { key: 'K4', delay: 6.5 },   // drop
];

const SOLUTION_CYCLE_SECONDS = 7;

// Tile anchor — left edge of lane 1 inside the source topic. Outer <g>
// applies this via SVG attribute; inner <g> animates the CSS transform.
const SOLUTION_ANCHOR_X = 60;
const SOLUTION_ANCHOR_Y = 165;

// translate-y delta from anchor to each filter slot (or drop bin).
const DY_LANE_1 = 0;
const DY_LANE_2 = 60;
const DY_LANE_3 = 120;
const DY_FILTER_1 = FILTER_ROWS[0].slotY - SOLUTION_ANCHOR_Y; // 35
const DY_FILTER_2 = FILTER_ROWS[1].slotY - SOLUTION_ANCHOR_Y; // 185
const DY_FILTER_3 = FILTER_ROWS[2].slotY - SOLUTION_ANCHOR_Y; // 335
const DY_DROP_BIN = DROP_BIN_Y + 18 - SOLUTION_ANCHOR_Y;      // 443

// translate-x markers along the source → filter → consumer path.
const DX_LANE_END   = 360; // tile-left at x=420 (well inside topic right edge x=480)
const DX_SLOT_ENTER = 484; // tile-left at x=544 (entering slot)
const DX_SLOT_EXIT  = 620; // tile-left at x=680 (exiting slot)
const DX_CONSUMER   = 880; // tile-left at x=940 (consumer entry)

const SOLUTION_CSS = `
  .sol-tile {
    animation-duration: ${SOLUTION_CYCLE_SECONDS}s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }
  .sol-tile-label { font-family: 'DM Mono', monospace; font-size: 13px; font-weight: 700; fill: white; }
  .sol-tile-portfolio-a1 { animation-name: sol-flow-f1; }
  .sol-tile-watchlist-a1 { animation-name: sol-flow-f2; }
  .sol-tile-watchlist-a2 { animation-name: sol-flow-f3; }
  .sol-tile-drop         { animation-name: sol-flow-drop; }

  /* All four animations share the same 0–78% snake-through-the-topic
     keyframes; they diverge only in where the tile goes after exiting
     lane 3 on the right. */

  @keyframes sol-flow-f1 {
    0%   { transform: translate(0px,                  ${DY_LANE_1}px); opacity: 0; }
    4%   { transform: translate(15px,                 ${DY_LANE_1}px); opacity: 1; }
    22%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_1}px); opacity: 1; }
    28%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_2}px); opacity: 1; }
    50%  { transform: translate(0px,                  ${DY_LANE_2}px); opacity: 1; }
    56%  { transform: translate(0px,                  ${DY_LANE_3}px); opacity: 1; }
    78%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_3}px); opacity: 1; }
    /* route UP to Filter 1 slot */
    83%  { transform: translate(440px,                85px);            opacity: 1; }
    87%  { transform: translate(${DX_SLOT_ENTER}px,   ${DY_FILTER_1}px); opacity: 1; }
    91%  { transform: translate(${DX_SLOT_EXIT}px,    ${DY_FILTER_1}px); opacity: 1; }
    98%  { transform: translate(${DX_CONSUMER}px,     ${DY_FILTER_1}px); opacity: 1; }
    100% { transform: translate(${DX_CONSUMER}px,     ${DY_FILTER_1}px); opacity: 0; }
  }

  @keyframes sol-flow-f2 {
    0%   { transform: translate(0px,                  ${DY_LANE_1}px); opacity: 0; }
    4%   { transform: translate(15px,                 ${DY_LANE_1}px); opacity: 1; }
    22%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_1}px); opacity: 1; }
    28%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_2}px); opacity: 1; }
    50%  { transform: translate(0px,                  ${DY_LANE_2}px); opacity: 1; }
    56%  { transform: translate(0px,                  ${DY_LANE_3}px); opacity: 1; }
    78%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_3}px); opacity: 1; }
    /* route slightly DOWN to Filter 2 slot */
    83%  { transform: translate(440px,                155px);           opacity: 1; }
    87%  { transform: translate(${DX_SLOT_ENTER}px,   ${DY_FILTER_2}px); opacity: 1; }
    91%  { transform: translate(${DX_SLOT_EXIT}px,    ${DY_FILTER_2}px); opacity: 1; }
    98%  { transform: translate(${DX_CONSUMER}px,     ${DY_FILTER_2}px); opacity: 1; }
    100% { transform: translate(${DX_CONSUMER}px,     ${DY_FILTER_2}px); opacity: 0; }
  }

  @keyframes sol-flow-f3 {
    0%   { transform: translate(0px,                  ${DY_LANE_1}px); opacity: 0; }
    4%   { transform: translate(15px,                 ${DY_LANE_1}px); opacity: 1; }
    22%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_1}px); opacity: 1; }
    28%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_2}px); opacity: 1; }
    50%  { transform: translate(0px,                  ${DY_LANE_2}px); opacity: 1; }
    56%  { transform: translate(0px,                  ${DY_LANE_3}px); opacity: 1; }
    78%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_3}px); opacity: 1; }
    /* route DOWN past Filter 1 and 2 into Filter 3 slot */
    83%  { transform: translate(440px,                240px);           opacity: 1; }
    87%  { transform: translate(${DX_SLOT_ENTER}px,   ${DY_FILTER_3}px); opacity: 1; }
    91%  { transform: translate(${DX_SLOT_EXIT}px,    ${DY_FILTER_3}px); opacity: 1; }
    98%  { transform: translate(${DX_CONSUMER}px,     ${DY_FILTER_3}px); opacity: 1; }
    100% { transform: translate(${DX_CONSUMER}px,     ${DY_FILTER_3}px); opacity: 0; }
  }

  @keyframes sol-flow-drop {
    0%   { transform: translate(0px,                  ${DY_LANE_1}px); opacity: 0; }
    4%   { transform: translate(15px,                 ${DY_LANE_1}px); opacity: 1; }
    22%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_1}px); opacity: 1; }
    28%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_2}px); opacity: 1; }
    50%  { transform: translate(0px,                  ${DY_LANE_2}px); opacity: 1; }
    56%  { transform: translate(0px,                  ${DY_LANE_3}px); opacity: 1; }
    78%  { transform: translate(${DX_LANE_END}px,     ${DY_LANE_3}px); opacity: 1; }
    /* bypass all filters and arc down into the drop bin */
    82%  { transform: translate(380px,                280px);           opacity: 1; }
    88%  { transform: translate(480px,                430px);           opacity: 0.75; filter: saturate(0.4); }
    94%  { transform: translate(540px,                ${DY_DROP_BIN}px); opacity: 0.35; filter: saturate(0); }
    100% { transform: translate(540px,                ${DY_DROP_BIN}px); opacity: 0; filter: saturate(0); }
  }

  .sol-consumer-ring {
    animation: sol-ring-pulse 1.4s ease-in-out infinite alternate;
  }
  @keyframes sol-ring-pulse {
    0%   { opacity: 0.45; }
    100% { opacity: 0.95; }
  }

  .sol-drop-arrow {
    stroke-dasharray: 4 4;
    animation: sol-drop-arrow-march 1.2s linear infinite;
  }
  @keyframes sol-drop-arrow-march {
    0%   { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: -16; }
  }
`;

function SolutionTile({ kClass, delay }: { kClass: KeyClass; delay: number }) {
  const tgt = branchFor(kClass);
  const cls = `sol-tile sol-tile-${tgt}`;
  const style: CSSProperties = { animationDelay: `${delay}s` };
  // Outer <g> carries the anchor via SVG attribute; inner <g> animates
  // the CSS transform. Required because CSS transform overrides the
  // SVG transform attribute on the same element.
  return (
    <g transform={`translate(${SOLUTION_ANCHOR_X} ${SOLUTION_ANCHOR_Y})`}>
      <g className={cls} style={style}>
        <rect width={52} height={36} rx={6} fill={KEY_FILL[kClass]} stroke={KEY_STROKE[kClass]} strokeWidth={1.5} />
        <text x={26} y={23} className="sol-tile-label" textAnchor="middle">{kClass}</text>
      </g>
    </g>
  );
}

/** One filter row + its consumer + its pipe. The slot lives at slotY and
    the consumer is vertically centered on the slot's tile-center. */
function FilterAndConsumer({ row }: { row: FilterRow }) {
  const filterCenterX = FILTER_X + FILTER_WIDTH / 2;       // x=610
  const slotCenterY   = row.slotY + 18;
  const consumerY     = slotCenterY - CONSUMER_HEIGHT / 2; // align center of consumer with slot center
  const consumerCenterX = CONSUMER_X + CONSUMER_WIDTH / 2; // x=1060
  const acceptsLabel = row.accepts.join(' · ');

  // Sample-badge layout: 1 badge centered, or 2 badges side-by-side centered.
  const badgeW = 38;
  const badgeH = 32;
  const badgeY = row.y + 30;
  const badgeGap = 6;
  const badgeRowW =
    row.accepts.length === 1
      ? badgeW
      : row.accepts.length * badgeW + (row.accepts.length - 1) * badgeGap;
  const badgeStartX = filterCenterX - badgeRowW / 2;

  return (
    <g>
      {/* Pipe from filter slot to consumer */}
      <line
        x1={FILTER_X + FILTER_WIDTH}
        y1={slotCenterY}
        x2={CONSUMER_X}
        y2={slotCenterY}
        stroke="#9FA3A8"
        strokeWidth={2}
        strokeDasharray="4 4"
      />

      {/* Filter outer frame */}
      <rect x={FILTER_X} y={row.y} width={FILTER_WIDTH} height={FILTER_HEIGHT} rx={12} fill="#FFFFFF" stroke="#0070D9" strokeWidth={2.5} />

      {/* ACCEPTS legend header */}
      <text x={filterCenterX} y={row.y + 18} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#0C5FB7" letterSpacing={1.8}>
        ACCEPTS · {acceptsLabel}
      </text>

      {/* Sample badge(s) — one per accepted key */}
      {row.accepts.map((k, i) => {
        const bx = badgeStartX + i * (badgeW + badgeGap);
        return (
          <g key={k}>
            <rect x={bx} y={badgeY} width={badgeW} height={badgeH} rx={5} fill={KEY_FILL[k]} stroke={KEY_STROKE[k]} strokeWidth={1.5} />
            <text x={bx + badgeW / 2} y={badgeY + 21} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={13} fontWeight={700} fill="white">{k}</text>
          </g>
        );
      })}

      {/* Slot — cutout where matching tiles pass through */}
      <rect
        x={FILTER_SLOT_X}
        y={row.slotY}
        width={FILTER_SLOT_WIDTH}
        height={36}
        rx={4}
        fill="none"
        stroke="#0070D9"
        strokeWidth={1.5}
        strokeDasharray="3 3"
      />

      {/* Output topic / consumer box */}
      <rect className="sol-consumer-ring" x={CONSUMER_X - 10} y={consumerY - 10} width={CONSUMER_WIDTH + 20} height={CONSUMER_HEIGHT + 20} rx={14}
            fill="none" stroke="#0070D9" strokeWidth={2.5} strokeDasharray="6 4" />
      <rect x={CONSUMER_X} y={consumerY} width={CONSUMER_WIDTH} height={CONSUMER_HEIGHT} rx={10} fill="#FFFFFF" stroke="#202939" strokeWidth={2} />
      <text x={consumerCenterX} y={consumerY + 38} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={18} fontWeight={700} fill="#202939">
        {row.topicName}
      </text>
      <text x={consumerCenterX} y={consumerY + 68} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fill="#0C5FB7" letterSpacing={1.2}>
        KEYS · {row.keysLabel}
      </text>
    </g>
  );
}

export function SolutionDiagram() {
  return (
    <svg
      viewBox="0 0 1200 720"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Animated diagram: a multi-key source topic streams keys via a 3-lane snake. Three filters in the middle each declare which keys they accept and route matches to a private output topic per consumer; all other keys arc down into a single Discarded bin."
      style={{ width: '100%', height: 'auto', maxHeight: 720 }}
    >
      <style>{SOLUTION_CSS}</style>

      {/* ─── Source topic — tiles snake through 3 lanes here ──────────── */}
      <g>
        <rect x={40} y={140} width={440} height={210} rx={16} fill="#F4F2EE" stroke="#5B6770" strokeWidth={2} />
        <text x={260} y={128} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={22} fontWeight={700} fill="#202939">
          Source topic
        </text>
        <text x={260} y={372} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fill="#5B6770" letterSpacing={1.5}>
          350 KEYS · CONTINUOUS
        </text>
      </g>

      {/* ─── Traveling tiles — drawn before the filter frames so the slot
              border reads ON TOP of a tile passing through. ───────────── */}
      {SOLUTION_TILES.map((t, i) => (
        <SolutionTile key={i} kClass={t.key} delay={t.delay} />
      ))}

      {/* ─── Three filter rows ─────────────────────────────────────────── */}
      {FILTER_ROWS.map((row) => (
        <FilterAndConsumer key={row.target} row={row} />
      ))}

      {/* ─── Drop path — single curved arrow from below source to bin ─── */}
      <path className="sol-drop-arrow" d="M 460 360 Q 460 480 620 595" fill="none" stroke="#B86B85" strokeWidth={2.5} />
      <polygon points="620,595 610,587 610,603" fill="#B86B85" />

      {/* ─── Discarded bin (below the filter column) ─────────────────── */}
      <g>
        <rect x={DROP_BIN_X} y={DROP_BIN_Y} width={DROP_BIN_WIDTH} height={DROP_BIN_HEIGHT} rx={10} fill="#FBE8EE" stroke="#B86B85" strokeWidth={1.5} strokeDasharray="3 3" />
        <text x={DROP_BIN_X + DROP_BIN_WIDTH / 2} y={DROP_BIN_Y + 30} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#8B3F58" letterSpacing={1.5}>
          DISCARDED
        </text>
        <text x={DROP_BIN_X + DROP_BIN_WIDTH / 2} y={DROP_BIN_Y + 55} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={13} fill="#8B3F58">
          keys no filter accepts
        </text>
      </g>

      {/* ─── Caption ──────────────────────────────────────────────────── */}
      <text x={600} y={702} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={18} fill="#2F3A45">
        One source. Three filters. Three personalized streams — fan-out happens at the broker.
      </text>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   PriorArtTimeline — seven products that shipped broker-side filtering,
   1997 → 2019, on a solid rail; Apache Kafka stranded on a dashed
   "filed 2017 → still open" lane. Teal = attribute/predicate surface
   (JMS selectors, SNS JSON policies); blue = hierarchical token routing.
   ───────────────────────────────────────────────────────────────────────── */
type PriorArtSide = 'above' | 'below';
type PriorArtCap = 'sql' | 'hot' | 'stream';
type CapState = boolean | 'delay';
type PriorArtMilestone = {
  k: string; x: number; year: string; name: string;
  sql: CapState; hot: CapState; stream: CapState;
  surface: string; ex: string; side: PriorArtSide; align?: 'right';
};

/* David's original surface + example per system are kept; the three capability
   icons (sql = SQL-style selector · hot = hot-reload · stream = durable log)
   replace the old family label. The gap: no system lights all three — and none
   speaks the Kafka protocol (the footnote). 'delay' = supported but slow (SNS). */
const PRIOR_ART_MILESTONES: ReadonlyArray<PriorArtMilestone> = [
  { k: 'tibco', x: 2,  year: '1997', name: 'TIBCO RV',         sql: false, hot: false,   stream: false, surface: 'Subject tokens',      ex: 'STOCK.US.>',          side: 'above' },
  { k: 'jms',   x: 14, year: '2001', name: 'JMS',              sql: true,  hot: false,   stream: false, surface: 'Message selectors',   ex: "symbol = 'AAPL'",     side: 'below' },
  { k: 'sol',   x: 24, year: '2004', name: 'Solace',           sql: true,  hot: false,   stream: true,  surface: 'Topic subscriptions', ex: 'stocks.usd.>',        side: 'above' },
  { k: 'rmq',   x: 34, year: '2007', name: 'RabbitMQ',         sql: false, hot: false,   stream: false, surface: 'Routing keys',        ex: 'stock.usd.#',         side: 'below' },
  { k: 'mqtt',  x: 44, year: '2010', name: 'MQTT',             sql: false, hot: false,   stream: false, surface: 'Topic filters',       ex: 'stocks/usd/#',        side: 'above' },
  { k: 'nats',  x: 56, year: '2016', name: 'NATS',             sql: false, hot: false,   stream: true,  surface: 'Subject tokens',      ex: 'prices.us.>',         side: 'below' },
  { k: 'sns',   x: 68, year: '2019', name: 'Amazon SNS',       sql: false, hot: 'delay', stream: false, surface: 'Filter policies',     ex: '{"symbol":["AAPL"]}', side: 'above' },
  { k: 'rmq25', x: 92, year: '2025', name: 'RabbitMQ Streams', sql: true,  hot: false,   stream: true,  surface: 'SQL filter expressions', ex: 'price > 50000',    side: 'below', align: 'right' },
];

const CAP_META: Record<PriorArtCap, { label: string; node: JSX.Element }> = {
  sql:    { label: 'SQL-style selector',        node: (<><path d="M9 8 5 12l4 4" /><path d="m15 8 4 4-4 4" /></>) },
  hot:    { label: 'Hot-reload (no reconnect)', node: (<><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v5h-5" /></>) },
  stream: { label: 'Streaming · durable log',   node: (<><rect x="4" y="5" width="16" height="3.4" rx="1.5" /><rect x="4" y="10.3" width="16" height="3.4" rx="1.5" /><rect x="4" y="15.6" width="16" height="3.4" rx="1.5" /></>) },
};

function CapBadge({ type, on }: { type: PriorArtCap; on: CapState }) {
  const m = CAP_META[type];
  const lit = on === true || on === 'delay';
  const label = on === 'delay' ? `${m.label}: yes, but delayed` : `${m.label}: ${on ? 'yes' : 'no'}`;
  return (
    <span
      className="optA-cap"
      title={label}
      aria-label={label}
      style={{ color: lit ? 'var(--sn-teal-5)' : 'var(--sn-grey-4)', opacity: lit ? 1 : 0.4 }}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {m.node}
      </svg>
      {on === 'delay' && (
        <svg className="optA-cap-clock" viewBox="0 0 24 24" width="11" height="11" fill="var(--sn-grey-1)" stroke="var(--sn-grey-6)" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </svg>
      )}
    </span>
  );
}

export function PriorArtTimeline() {
  return (
    <div className="optA">
      <div className="optA-legend">
        <span><CapBadge type="sql" on /> SQL-style selector</span>
        <span><CapBadge type="hot" on /> Hot-reload</span>
        <span><CapBadge type="stream" on /> Streaming · durable log</span>
      </div>
      <div className="optA-axis">
        <div className="optA-rail" />
        <div className="optA-today">Today · 2026</div>

        {PRIOR_ART_MILESTONES.map((m, i) => (
          <div key={m.k} className="optA-node" style={{ left: `${m.x}%` }}>
            <div
              className="optA-dot optA-dot--blue"
              style={{
                animationDelay: `${660 + i * 480}ms`,
                // Right-anchored cards (e.g. RabbitMQ Streams 2025) hang to the
                // LEFT of the node, so slide the dot left to sit over the card's
                // centre. `left` (not transform) — p3-pop animates transform.
                ...(m.align === 'right'
                  ? { position: 'relative', left: 'calc(32px - clamp(150px, 15vw, 205px) / 2)' }
                  : {}),
              }}
            />
            <div
              className={`optA-card optA-card--${m.side}`}
              style={{ animationDelay: `${1140 + i * 480}ms`, ...(m.align === 'right' ? { left: 'auto', right: '-32px' } : {}) }}
            >
              <div className="optA-year optA-year--blue">{m.year}</div>
              <div className="optA-name">{m.name}</div>
              <div className="optA-caps">
                <CapBadge type="sql" on={m.sql} />
                <CapBadge type="hot" on={m.hot} />
                <CapBadge type="stream" on={m.stream} />
              </div>
              <div className="optA-surface">{m.surface}</div>
              <code className="optA-ex">{m.ex}</code>
            </div>
          </div>
        ))}

        {/* Kafka — filed 2017, still open today (the lone hold-out) */}
        <div className="optA-kafka" style={{ left: '68%', right: '2%' }} />
        <div className="optA-kfiled" style={{ left: '68%' }} />
        <div className="optA-klbl" style={{ left: '68%', animationDelay: '4500ms' }}>Filed 2017</div>
        <div className="optA-kopen" style={{ left: '98%' }} />
        <div className="optA-klbl" style={{ left: '98%', color: 'var(--sn-grey-6)', animationDelay: '6150ms' }}>Still open</div>
        <div className="optA-kafka-card">
          <div className="optA-kafka-name">Apache Kafka</div>
          <div className="optA-kafka-tag" style={{ marginLeft: 'auto' }}><span className="blip" />KAFKA-6020 · needs KIP</div>
        </div>
      </div>
      <div className="optA-foot">…and none of them speaks the Kafka protocol</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Kafka6020Timeline — Kafka's own eight-year stall, same visual language but
   a single muted "stall" rail; the 2026 "Today" marker turns blue (the
   live-demo turn). Sits on the slide that follows the prior-art slide.
   ───────────────────────────────────────────────────────────────────────── */
type KafkaTone = 'grey' | 'blue';
type KafkaMilestone = {
  k: string; x: number; date: string; label: string; sub: string;
  tone: KafkaTone; side: PriorArtSide; align?: 'right';
};

const KAFKA_6020_MILESTONES: ReadonlyArray<KafkaMilestone> = [
  { k: 'filed',  x: 3,  date: '2017',      label: 'Filed',             sub: 'Initial ask for broker-side filtering',       tone: 'grey', side: 'above' },
  { k: 'spor',   x: 27, date: '2018–2022', label: 'Sporadic interest', sub: 'Comments accumulate; no proposal',            tone: 'grey', side: 'below' },
  { k: 'renew',  x: 51, date: '2023',      label: 'Renewed activity',  sub: 'Spike in discussion threads',                 tone: 'grey', side: 'above' },
  { k: 'silent', x: 73, date: '2024–2025', label: 'Silence',           sub: 'No comments, no KIP draft',                   tone: 'grey', side: 'below' },
  { k: 'today',  x: 97, date: '2026',      label: 'Today',             sub: 'Still “needs KIP” — we show it working live', tone: 'blue', side: 'above', align: 'right' },
];

export function Kafka6020Timeline() {
  return (
    <div className="optA optA--stall">
      <div className="optA-axis">
        <div className="optA-rail" />
        {KAFKA_6020_MILESTONES.map((m, i) => {
          const cardStyle: CSSProperties = {
            animationDelay: `${1140 + i * 480}ms`,
            ...(m.align === 'right' ? { left: 'auto', right: '-2px', textAlign: 'left' } : {}),
          };
          return (
            <div key={m.k} className="optA-node" style={{ left: `${m.x}%` }}>
              <div className={`optA-dot optA-dot--${m.tone}`} style={{ animationDelay: `${660 + i * 480}ms` }} />
              <div className={`optA-card optA-card--${m.side}`} style={cardStyle}>
                <div className={`optA-year optA-year--${m.tone}`}>{m.date}</div>
                <div className="optA-name">{m.label}</div>
                <div className="optA-surface">{m.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Count-up hook — eases 0 → target on mount (restarts on remount via key). */
function useCountUp(target: number, { duration = 1400, delay = 300 }: { duration?: number; delay?: number } = {}): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf = 0, start = 0;
    const t = setTimeout(() => {
      const tick = (now: number) => {
        if (!start) start = now;
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(target * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return val;
}

/* ════════════════════════════════════════════════════════════════════════
   useSlideActivation — restart animations on slide re-entry
   ────────────────────────────────────────────────────────────────────────
   The framework keeps every slide mounted and toggles an `.is-active`
   class on the nearest `.slide` ancestor (see DeckApp.tsx line 76). CSS
   keyframe animations + React useEffect hooks fire on mount and don't
   re-trigger on visibility toggles — so when the user navigates away
   and comes back, the entry animations have already finished and nothing
   plays.

   This hook returns:
     - a ref to attach to the diagram's top-level element
     - an integer `activation` that increments every time the parent
       `.slide` transitions from inactive → active

   Pattern: attach the ref to a wrapper, key the inner content with the
   activation counter. React remounts the inner content on each
   activation, restarting CSS animations (new DOM nodes) and re-running
   React effects (like `useCountUp`).

   The first activation does NOT bump — the component just mounted, so
   its animations are already playing from t=0. Only subsequent inactive
   → active transitions bump.
   ════════════════════════════════════════════════════════════════════════ */
export function useSlideActivation(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [activation, setActivation] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const slide = el.closest('.slide');
    if (!slide) return;

    let prev = slide.classList.contains('is-active');

    const obs = new MutationObserver(() => {
      const cur = slide.classList.contains('is-active');
      if (cur && !prev) setActivation((c) => c + 1);
      prev = cur;
    });
    obs.observe(slide, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  return [ref, activation];
}

/* ════════════════════════════════════════════════════════════════════════
   BEAT 01 (slide 2) — DumbBrokerDiagram
   By default a broker delivers the whole stream to every subscriber; the
   consumer does the sorting, paying for deserialize on every message before
   it can throw most of them away. No broker-side filter exists yet — the
   default across messaging systems. Generic by design; the Kafka-specific
   wrapper lives below (parked). viewBox 1200×520.
   Same nested-<g> pattern as ProblemDiagram: outer <g> carries the anchor via
   the SVG transform attribute, inner <g> carries the CSS-keyframe transform.
   ════════════════════════════════════════════════════════════════════════ */
/* Durations + per-tile delays slowed 2.5× from Claude Design's
   exploration defaults (was 5×; halved per follow-up). */
const DUMB_TILES: ReadonlyArray<{ key: KeyClass; d: number }> = [
  { key: 'K3', d: 0.0 },    { key: 'K1', d: 1.125 }, { key: 'K4', d: 2.25 },  { key: 'K7', d: 3.375 },
  { key: 'K2', d: 4.5 },    { key: 'K0', d: 5.625 }, { key: 'K5', d: 6.75 },  { key: 'K6', d: 7.875 },
  { key: 'K2', d: 9.0 },    { key: 'K1', d: 10.125 },{ key: 'K4', d: 11.25 }, { key: 'K3', d: 12.375 },
  { key: 'K7', d: 13.5 },   { key: 'K0', d: 14.625 },
];
const DUMB_CYCLE = 15.75; // was 6.3 → 31.5 → 15.75 (2.5× original)
const DUMB_WANTED: KeyClass = 'K1';

const DUMB_CSS = `
  .dumb-tile { animation-duration: ${DUMB_CYCLE}s; animation-iteration-count: infinite; animation-timing-function: linear; }
  .dumb-tile-label { font-family:'DM Mono',monospace; font-size:13px; font-weight:700; fill:white; }
  .dumb-tile-keep { animation-name: dumb-keep; }
  .dumb-tile-drop { animation-name: dumb-drop; }
  /* Travel the full wire (x 0→560), pass the deserialize gate (~62%),
     THEN branch — kept tiles settle, dropped tiles fall into the bin.
     The split happens AFTER the gate: the CPU has already paid. */
  @keyframes dumb-keep {
    0%{transform:translate(0px,0px);opacity:0;} 5%{transform:translate(14px,0px);opacity:1;}
    58%{transform:translate(560px,0px);opacity:1;}
    66%{transform:translate(636px,0px);opacity:1;}
    80%{transform:translate(700px,-46px);opacity:1;} 100%{transform:translate(700px,-46px);opacity:0;}
  }
  @keyframes dumb-drop {
    0%{transform:translate(0px,0px);opacity:0;} 5%{transform:translate(14px,0px);opacity:1;}
    58%{transform:translate(560px,0px);opacity:1;}
    66%{transform:translate(636px,0px);opacity:1;}
    78%{transform:translate(672px,40px);opacity:0.8;} 90%{transform:translate(706px,104px);opacity:0.4;filter:saturate(0.3);}
    100%{transform:translate(720px,120px);opacity:0;filter:saturate(0);}
  }
  .dumb-gate { animation: dumb-gate-pulse 2.5s ease-in-out infinite alternate; }
  @keyframes dumb-gate-pulse { 0%{opacity:0.55;} 100%{opacity:1;} }
  .dumb-disk line { animation: dumb-disk-scan 4s linear infinite; }
  @keyframes dumb-disk-scan { 0%{opacity:0.25;} 50%{opacity:0.9;} 100%{opacity:0.25;} }
`;

function DumbTile({ kClass, d }: { kClass: KeyClass; d: number }) {
  const keep = kClass === DUMB_WANTED;
  return (
    <g transform="translate(214 232)">
      <g className={`dumb-tile ${keep ? 'dumb-tile-keep' : 'dumb-tile-drop'}`} style={{ animationDelay: `${d}s` }}>
        <rect width={48} height={34} rx={6} fill={KEY_FILL[kClass]} stroke={KEY_STROKE[kClass]} strokeWidth={1.5} />
        <text x={24} y={22} className="dumb-tile-label" textAnchor="middle">{kClass}</text>
      </g>
    </g>
  );
}

type DumbBrokerProps = { badge?: string; brokerSub?: string; caption?: string; aria?: string };

/* Inner body — the actual SVG. Keyed by activation in the wrapper below
   so CSS animations restart cleanly each time the slide re-enters. */
function DumbBrokerDiagramBody({
  badge = 'THE DEFAULT EVERYWHERE',
  brokerSub = 'SHIPS EVERYTHING',
  caption = 'The broker ships every message. The client sorts it out.',
  aria = 'By default a messaging broker delivers the whole 35,000 msg/s stream to every consumer. The consumer deserializes every message, keeps about 500, and discards the rest after the CPU has already paid.',
}: DumbBrokerProps = {}) {
  return (
    <svg viewBox="0 0 1200 520" xmlns="http://www.w3.org/2000/svg" role="img"
      aria-label={aria}
      style={{ width: '100%', height: 'auto', maxHeight: 540 }}>
      <style>{DUMB_CSS}</style>

      {/* ── Broker — delivers the full stream by default ────────────── */}
      <g>
        <rect x={40} y={150} width={170} height={200} rx={16} fill="#082B3E" stroke="#082B3E" strokeWidth={2} />
        <text x={125} y={186} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={19} fontWeight={700} fill="#FFFFFF">Broker</text>
        <g className="dumb-disk">
          {[0,1,2,3,4,5].map((i) => (
            <line key={i} x1={64} y1={210 + i * 22} x2={186} y2={210 + i * 22} stroke="#74B0F4" strokeWidth={5} strokeLinecap="round" />
          ))}
        </g>
        <text x={125} y={372} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fill="#9FA3A8" letterSpacing={1.4}>{brokerSub}</text>
      </g>

      {/* Framing badge — pill centered over the broker box (broker centerX = 125). */}
      <g>
        <rect x={13} y={104} width={224} height={30} rx={15} fill="#E8F2FD" stroke="#0070D9" strokeWidth={1.5} />
        <text x={125} y={124} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#0E4A8E" letterSpacing={1.6}>{badge}</text>
      </g>

      {/* ── The wire — full firehose, no filter ─────────────────────── */}
      <g>
        <rect x={214} y={222} width={560} height={54} rx={8} fill="#F4F2EE" stroke="#D7CDC3" strokeWidth={1.5} />
        <text x={494} y={300} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fontWeight={600} fill="#697586" letterSpacing={1.4}>THE WIRE · 35,000 MSG/S · EVERY BYTE, EVERY CONSUMER</text>
      </g>
      {DUMB_TILES.map((t, i) => <DumbTile key={i} kClass={t.key} d={t.d} />)}

      {/* ── Consumer — pays deserialize on EVERYTHING, then sorts ─────
           Consumer box + its inner pills shifted +30px from the handoff
           defaults (handoff was 838 → -30 to 808 → +60 to 868) so the
           consumer sits clear of the deserialize gate label without the
           wire tiles clipping the box border. */}
      <g>
        <g className="dumb-gate">
          <rect x={788} y={196} width={16} height={108} rx={4} fill="#0070D9" />
        </g>
        <text x={796} y={186} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={700} fill="#0E4A8E" letterSpacing={1.2}>DESERIALIZE</text>
        <text x={796} y={324} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fill="#697586" letterSpacing={1}>CPU PAYS HERE</text>

        <rect x={868} y={150} width={300} height={210} rx={14} fill="#FFFFFF" stroke="#202939" strokeWidth={2} />
        <text x={1018} y={184} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={19} fontWeight={700} fill="#202939">Consumer</text>

        <rect x={892} y={200} width={252} height={56} rx={9} fill="#E8F2FD" stroke="#0070D9" strokeWidth={1.5} />
        <text x={1018} y={224} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={14} fontWeight={700} fill="#0E4A8E">~500 msg/s kept</text>
        <text x={1018} y={244} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fill="#0E4A8E" letterSpacing={1}>THE KEYS IT WANTED</text>

        <rect x={892} y={268} width={252} height={72} rx={9} fill="#FBE8EE" stroke="#B86B85" strokeWidth={1.5} strokeDasharray="3 3" />
        <text x={1018} y={293} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fill="#8B3F58" letterSpacing={1.3}>DISCARDED</text>
        <text x={1018} y={314} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={15} fontWeight={700} fill="#8B3F58">~34,500 msg/s</text>
        <text x={1018} y={332} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={11.5} fill="#8B3F58">deserialized, then thrown away</text>
      </g>

      <text x={600} y={420} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={20} fontWeight={600} fill="#202939">
        {caption}
      </text>
      <text x={600} y={452} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={16} fill="#5B6770">
        Filtering happens after the network hop, after deserialize, after the CPU has already paid.
      </text>
    </svg>
  );
}

/* Public wrapper — attaches the activation ref and remounts the SVG body
   each time the slide re-enters so the keyframe animations replay. */
export function DumbBrokerDiagram(props: DumbBrokerProps = {}) {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <DumbBrokerDiagramBody key={activation} {...props} />
    </div>
  );
}

/* PARKED for the later "Kafka vs. others" slide — Confluent's own
   "dumb broker · by design" phrasing. NOT used on slides 2–4. Same diagram,
   Kafka-flavored labels. */
export function KafkaDumbBrokerDiagram() {
  return (
    <DumbBrokerDiagram
      badge="DUMB BROKER · BY DESIGN"
      brokerSub="SEQUENTIAL LOG READ"
      caption="The broker dumps the whole log. The client sorts it out."
      aria="Kafka is a deliberately dumb broker: a sequential log reader that dumps the whole 35,000 msg/s stream over the wire by design. The consumer deserializes every message, keeps about 500, and discards the rest after the CPU has already paid."
    />
  );
}

/* ════════════════════════════════════════════════════════════════════════
   BEAT 02 (slide 3) — CostDonut
   "What you pay for vs. what you keep." A ring of the 35,000 msg/s the broker
   delivers to every consumer; ~98.6% (34,500 msg/s) is discarded. The dollar
   consequence is the linked callout, not a conflated number. Styling lives in
   styles.css (.donut-* + p3-rise).
   ════════════════════════════════════════════════════════════════════════ */
/* Numbers derived from the scenario set up on the slide:
     500 keys on the topic · 35,000 msg/s · consumer subscribes to 3 keys.
   With evenly-distributed keys: 3/500 = 0.6% kept, 99.4% discarded.
   Count-up + entry timings slowed 5× from Claude Design's exploration. */
function CostDonutInner() {
  const R = 150, C = 2 * Math.PI * R;
  const wasteFrac = 497 / 500; // 0.994
  const pct = useCountUp(99.4, { duration: 7500, delay: 3250 });
  const wasteDash = useCountUp(C * wasteFrac, { duration: 8000, delay: 2500 });
  return (
    <>
      <div className="donut-figure">
        <svg viewBox="0 0 380 380" role="img" aria-label="Donut: of 35,000 messages per second on a topic with 500 keys, the consumer subscribes to 3 keys — 99.4 percent (about 34,790 msg/s, 497 of 500 keys) is discarded.">
          <circle cx={190} cy={190} r={R} fill="none" stroke="#0070D9" strokeWidth={40} />
          <circle cx={190} cy={190} r={R} fill="none" stroke="#E4A6BC" strokeWidth={40}
            strokeDasharray={`${wasteDash} ${C}`} strokeLinecap="butt"
            transform="rotate(-90 190 190)" />
          <text x={190} y={172} textAnchor="middle" fontFamily="Funnel Display, sans-serif" fontSize={58} fontWeight={600} fill="#8B3F58">{pct.toFixed(1)}%</text>
          <text x={190} y={206} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={13} fill="#8B3F58" letterSpacing={2}>DISCARDED</text>
          <text x={190} y={230} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fill="#5B6770" letterSpacing={1.2}>~34,790 MSG/S</text>
        </svg>
      </div>

      <div className="donut-side">
        <div className="donut-legend">
          <div className="donut-leg-row">
            <span className="donut-swatch donut-swatch--keep" />
            <div>
              <div className="donut-leg-name">What you keep</div>
              <div className="donut-leg-sub">3 keys · ~210 msg/s — the only keys this consumer subscribed to</div>
            </div>
          </div>
          <div className="donut-leg-row">
            <span className="donut-swatch donut-swatch--waste" />
            <div>
              <div className="donut-leg-name">What you pay for</div>
              <div className="donut-leg-sub">497 keys · ~34,790 msg/s deserialized and thrown away</div>
            </div>
          </div>
        </div>
        <div className="donut-dollar">
          <div className="donut-dollar-num">3 of 500<span className="donut-dollar-unit"> kept</span></div>
          <div className="donut-dollar-cap">The savings are selectivity-driven, not throughput-driven — this 99.4% ratio holds whether the topic runs at 35,000 msg/s or 35. The dollar receipt for a measured perf workload lands later in the talk.</div>
        </div>
      </div>
    </>
  );
}

/* Public wrapper — attaches activation ref and remounts the inner body
   so useCountUp re-runs and the .donut-* p3-rise CSS animations replay. */
export function CostDonut() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} className="donut">
      <CostDonutInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   BEAT 03 (slide 4) — EnvelopePile (three-stage flow)
   Each envelope visibly cycles through three stages so the audience can
   SEE the cost being paid:

     1) ARRIVE — emerge at broker, travel down the wire to the gate
     2) DESERIALIZE — pause at the gate. This is the slowest segment
        (~60% of the cycle) by design: every message pays 20–30 ms here,
        kept or dropped.
     3) FORK — branch to the consumer (kept) or the discard bin (dropped).

   The 2.4-seconds-late punchline lives in the .pile-foot text below the
   SVG; the foot is delay-revealed so it lands after the audience has
   watched the cycle play out at least once.
   ════════════════════════════════════════════════════════════════════════ */

/* Sequential, non-overlapping envelopes — only ONE envelope at the gate
   at any moment so the timer signal stays unambiguous. */
const PILE_CYCLE = 4; // seconds per envelope cycle
const PILE_ENVS: ReadonlyArray<{ keep: boolean; d: number; stamp: string }> = [
  { keep: false, d: 0,                  stamp: '09:30:00.024' },
  { keep: false, d: PILE_CYCLE * 1,     stamp: '09:30:00.051' },
  { keep: false, d: PILE_CYCLE * 2,     stamp: '09:30:00.078' },
  { keep: true,  d: PILE_CYCLE * 3,     stamp: '09:30:02.420' }, // the wanted one
];
const PILE_MACRO = PILE_CYCLE * PILE_ENVS.length; // 16s — full sequence

const PILE_CSS = `
  .pile-anim {
    animation-duration: ${PILE_MACRO}s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    /* backwards: respect 0% opacity BEFORE animation-delay elapses,
       so envelopes 1/2/3 don't show at the broker before their turn. */
    animation-fill-mode: backwards;
  }
  .pile-anim-keep { animation-name: pile-flow-keep; }
  .pile-anim-drop { animation-name: pile-flow-drop; }
  /* Each envelope owns a 4-second slot inside the 16s macro cycle. The
     envelope stays VISIBLE the whole time it's at the barrier — that's the
     synchronization anchor. Bar fill timing matches the envelope's stop:
     bar starts filling THE MOMENT envelope arrives, finishes filling THE
     MOMENT envelope departs.
     Per-envelope keyframe %s (slot = 0%–25% of the 16s cycle = 0–4s):
       0.00  – 1.25  %   spawn at broker (opacity 0 → 1)
       1.25  – 3.75  %   travel from broker to barrier
       3.75  – 22.50 %   STOPPED at barrier (3.0s · bar filling here)
       22.50 – 24.00 %   route to destination
       24.00 – 25.00 %   settle + fade
       25.00 – 100   %   invisible at destination, waiting next macro cycle */
  @keyframes pile-flow-keep {
    0%      { transform: translate(0px, 0px); opacity: 0; }
    1.25%   { transform: translate(15px, 0px); opacity: 1; }
    3.75%   { transform: translate(280px, 0px); opacity: 1; }
    22.5%   { transform: translate(280px, 0px); opacity: 1; }
    24%     { transform: translate(790px, -90px); opacity: 1; }
    25%     { transform: translate(790px, -90px); opacity: 0; }
    100%    { transform: translate(790px, -90px); opacity: 0; }
  }
  @keyframes pile-flow-drop {
    0%      { transform: translate(0px, 0px); opacity: 0; }
    1.25%   { transform: translate(15px, 0px); opacity: 1; }
    3.75%   { transform: translate(280px, 0px); opacity: 1; }
    22.5%   { transform: translate(280px, 0px); opacity: 1; }
    24%     { transform: translate(790px, 110px); opacity: 0.6; filter: saturate(0.3); }
    25%     { transform: translate(810px, 130px); opacity: 0; filter: saturate(0); }
    100%    { transform: translate(810px, 130px); opacity: 0; filter: saturate(0); }
  }
  /* The timer. Cycle = ${PILE_CYCLE}s (one envelope slot), loops 4× per
     macro cycle. Sync targets (inside each ${PILE_CYCLE}s cycle):
       0%   empty (envelope just spawned at broker)
       15%  envelope arrives at barrier → bar STARTS filling
       90%  envelope finishes deserialize → bar FULL
       100% envelope has routed → bar drained, empty for next envelope */
  .pile-bar-fill {
    transform-box: fill-box;
    transform-origin: 50% 100%;
    animation: pile-bar-fill-anim ${PILE_CYCLE}s linear infinite;
  }
  @keyframes pile-bar-fill-anim {
    0%   { transform: scaleY(0); }
    15%  { transform: scaleY(0); }
    90%  { transform: scaleY(1); }
    100% { transform: scaleY(0); }
  }
  /* Digital ms timer (above the barrier) — counts up during deserialize
     phase, resets per envelope. Five text frames stacked at the same
     coordinates; each frame's keyframes flip its opacity at the right
     moment so only ONE is visible at a time. Simpler and more reliable
     than CSS counter() animation in SVG <text>. */
  .pile-dt-frame { opacity: 0; animation: pile-dt-base ${PILE_CYCLE}s linear infinite; }
  /* Base keyframe is needed so each frame inherits the timeline; the per-frame
     animation-name below picks the actual opacity schedule. */
  @keyframes pile-dt-base { from { opacity: 0; } to { opacity: 0; } }
  .pile-dt-frame-0 { animation-name: pile-dt-show-0; }
  .pile-dt-frame-1 { animation-name: pile-dt-show-1; }
  .pile-dt-frame-2 { animation-name: pile-dt-show-2; }
  .pile-dt-frame-3 { animation-name: pile-dt-show-3; }
  .pile-dt-frame-4 { animation-name: pile-dt-show-4; }
  @keyframes pile-dt-show-0 { 0%,14.99% { opacity: 1; } 15%,89.99% { opacity: 0; } 90%,100% { opacity: 1; } }
  @keyframes pile-dt-show-1 { 0%,14.99% { opacity: 0; } 15%,29.99% { opacity: 1; } 30%,100% { opacity: 0; } }
  @keyframes pile-dt-show-2 { 0%,29.99% { opacity: 0; } 30%,44.99% { opacity: 1; } 45%,100% { opacity: 0; } }
  @keyframes pile-dt-show-3 { 0%,44.99% { opacity: 0; } 45%,59.99% { opacity: 1; } 60%,100% { opacity: 0; } }
  @keyframes pile-dt-show-4 { 0%,59.99% { opacity: 0; } 60%,89.99% { opacity: 1; } 90%,100% { opacity: 0; } }

`;

function PileEnvelope({ keep, d, stamp }: { keep: boolean; d: number; stamp: string }) {
  // Outer <g> anchors via SVG transform attribute (composes with the inner
  // CSS-animation transform — same nested-g pattern as ProblemDiagram).
  return (
    <g transform="translate(210 200)">
      <g className={`pile-anim ${keep ? 'pile-anim-keep' : 'pile-anim-drop'}`} style={{ animationDelay: `${d}s` }}>
        <rect width={130} height={44} rx={6} fill={keep ? '#E8F2FD' : '#FBE8EE'} stroke={keep ? '#0070D9' : '#B86B85'} strokeWidth={keep ? 2 : 1.5} />
        <path d="M8 8 L65 28 L122 8" fill="none" stroke={keep ? '#0C5FB7' : '#B86B85'} strokeWidth={1.6} strokeLinejoin="round" />
        <text x={65} y={38} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={500} fill={keep ? '#0C5FB7' : '#8B3F58'}>{stamp}</text>
      </g>
    </g>
  );
}

function EnvelopePileInner() {
  return (
    <>
      <svg viewBox="0 0 1200 480" xmlns="http://www.w3.org/2000/svg" role="img"
        aria-label="Three-stage message lifecycle: each envelope arrives from the broker, pauses 20-30 ms at the deserialize gate, then forks to either the consumer (kept) or the discard bin (dropped). The wanted envelope lands 2.4 seconds after the first because everything ahead of it had to be deserialized first."
        style={{ width: '100%', height: 'auto', maxHeight: 460 }}>
        <style>{PILE_CSS}</style>

        {/* Broker — message source on the left */}
        <g>
          <rect x={40} y={140} width={170} height={160} rx={14} fill="#082B3E" />
          <text x={125} y={186} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={19} fontWeight={700} fill="#FFFFFF">Broker</text>
          <text x={125} y={216} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#9FA3A8" letterSpacing={1.4}>35,000 MSG/S</text>
        </g>

        {/* Wire from broker to deserialize gate */}
        <rect x={210} y={218} width={410} height={30} rx={6} fill="#F4F2EE" stroke="#D7CDC3" strokeWidth={1} />

        {/* DESERIALIZE gate — the slow station every envelope must cross.
            The bar IS the timer: outline is always visible, the inner fill
            rises bottom→top during each envelope's deserialize phase, then
            drains the moment the envelope exits the other side. The digital
            counter above the bar reads out simulated ms (0 → 30) per
            message, resets per envelope. */}
        <g>
          {/* Digital ms timer above the bar — LCD background + counter frames */}
          <rect x={596} y={66} width={70} height={32} rx={4} fill="#082B3E" />
          <text x={631} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={15} fontWeight={700} fill="#5CFFB5" letterSpacing={0.5} className="pile-dt-frame pile-dt-frame-0">0 ms</text>
          <text x={631} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={15} fontWeight={700} fill="#5CFFB5" letterSpacing={0.5} className="pile-dt-frame pile-dt-frame-1">8 ms</text>
          <text x={631} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={15} fontWeight={700} fill="#5CFFB5" letterSpacing={0.5} className="pile-dt-frame pile-dt-frame-2">15 ms</text>
          <text x={631} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={15} fontWeight={700} fill="#5CFFB5" letterSpacing={0.5} className="pile-dt-frame pile-dt-frame-3">23 ms</text>
          <text x={631} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={15} fontWeight={700} fill="#5CFFB5" letterSpacing={0.5} className="pile-dt-frame pile-dt-frame-4">30 ms</text>

          {/* Bar — outline + animated fill */}
          <rect x={620} y={160} width={22} height={150} rx={5} fill="#E8F2FD" stroke="#0070D9" strokeWidth={1.5} />
          <rect x={620} y={160} width={22} height={150} rx={5} fill="#0070D9" className="pile-bar-fill" />
          <text x={631} y={148} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#0E4A8E" letterSpacing={1.4}>DESERIALIZE</text>
          <text x={631} y={328} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={500} fill="#697586" letterSpacing={1.2}>20–30 MS PER MESSAGE</text>
          <text x={631} y={346} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fill="#697586" letterSpacing={1.2}>PAID ON EVERY MESSAGE · KEEP OR DROP</text>
        </g>

        {/* Animated envelopes — rendered after wire/gate so they paint above */}
        {PILE_ENVS.map((e, i) => <PileEnvelope key={i} {...e} />)}

        {/* Consumer destination (kept envelopes land here) — upper right */}
        <g>
          <rect x={860} y={72} width={290} height={128} rx={12} fill="#FFFFFF" stroke="#202939" strokeWidth={2} />
          <text x={1005} y={108} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={18} fontWeight={700} fill="#202939">Consumer</text>
          <text x={1005} y={138} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={13} fill="#0C5FB7">acts on the keep</text>
          <text x={1005} y={164} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fill="#0C5FB7" letterSpacing={1.2}>3 KEYS · ~210 MSG/S</text>
        </g>

        {/* Discard bin (dropped envelopes fall here) — lower right */}
        <g>
          <rect x={860} y={272} width={290} height={128} rx={12} fill="#FBE8EE" stroke="#B86B85" strokeWidth={1.5} strokeDasharray="4 4" />
          <text x={1005} y={308} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={16} fontWeight={700} fill="#8B3F58">Discarded</text>
          <text x={1005} y={334} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={12} fill="#8B3F58">deserialized, then thrown away</text>
          <text x={1005} y={364} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fill="#8B3F58" letterSpacing={1.2}>497 KEYS · ~34,790 MSG/S</text>
        </g>

      </svg>

      <div className="pile-foot">
        <p className="pile-foot-txt">
          The message you wanted arrived on time. Your consumer reaches it
          <strong> 2.4 seconds late</strong> — it had to deserialize and discard everything queued in
          front of it first. The cost <em>compounds</em>: every unwanted message extends time-to-action
          for the wanted one behind it. Nothing in the client flags this.
        </p>
      </div>
    </>
  );
}

/* Public wrapper — attaches activation ref and remounts the inner body
   so the .pile-* p3-rise CSS animations replay on each slide re-entry. */
export function EnvelopePile() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} className="pile">
      <EnvelopePileInner key={activation} />
    </div>
  );
}

/* Climb a counter at ratePerSec for holdAfterS seconds, then hold (or, with
   loop, snap back to 0 and climb again — keeps the booth visual cycling).
   Resets on remount. (Distinct from the Problem-Beats `useCountUp`.) */
function useClimb(ratePerSec: number, holdAfterS: number, delay = 300, loop = false): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0, start = 0;
    const t = setTimeout(() => {
      const tick = (now: number) => {
        if (!start) start = now;
        let el = (now - start) / 1000;
        if (loop && el >= holdAfterS) { start = now; el = 0; } // restart at the cap
        const capped = loop ? el : Math.min(el, holdAfterS);
        setV(capped * ratePerSec);
        if (loop || capped < holdAfterS) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [ratePerSec, holdAfterS, delay, loop]);
  return v;
}
const fmtC = (n: number) => Math.round(n).toLocaleString('en-US');

const CMP_BLUE = '#0070D9', CMP_BLUE_STK = '#0C5FB7';
const CMP_GREY = '#C4C7CB', CMP_GREY_STK = '#9298A0';

/* Tile stream — ~1 in 6 wanted (one of the 5 tickers), reading as
   "mostly noise, occasional signal". */
const CMP_TILES: ReadonlyArray<{ w: boolean; d: number }> = [
  { w: false, d: 0.0 }, { w: true, d: 0.5 }, { w: false, d: 1.0 }, { w: false, d: 1.5 },
  { w: false, d: 2.0 }, { w: false, d: 2.5 }, { w: true, d: 3.0 }, { w: false, d: 3.5 },
  { w: false, d: 4.0 }, { w: false, d: 4.5 }, { w: false, d: 5.0 }, { w: true, d: 5.5 },
  { w: false, d: 6.0 }, { w: false, d: 6.5 },
];

function ConveyorTile({ anchor, cls, d, wanted }: { anchor: [number, number]; cls: string; d: number; wanted: boolean }) {
  return (
    <g transform={`translate(${anchor[0]} ${anchor[1]})`}>
      <g className={cls} style={{ animationDelay: `${d}s` }}>
        <rect x={-20} y={-14} width={40} height={28} rx={6}
          fill={wanted ? CMP_BLUE : CMP_GREY} stroke={wanted ? CMP_BLUE_STK : CMP_GREY_STK} strokeWidth={1.5} />
      </g>
    </g>
  );
}

export function ConveyorCompare() {
  const wasted = useClimb(34500, 30, 300, true); // loops at 1,035,000 (34.5k/s × 30s)
  return (
    <div className="cmp">
      <svg className="cmp-svg" viewBox="0 0 1280 640" xmlns="http://www.w3.org/2000/svg" role="img"
        aria-label="Two conveyor belts from one source. The prior-art belt has a sorting arm at the broker that flicks unwanted messages off; only wanted ones reach a small inbox. The Kafka belt has no arm; every message rides into an inbox that overflows while the consumer processes one at a time.">

        {/* broker-side barrier — encloses the source topic + belts + filter;
            the belts cross its right edge = the broker→client network boundary */}
        <rect x={8} y={88} width={862} height={516} rx={18} fill="none" stroke="#5B6770" strokeWidth={2} strokeDasharray="8 8" />
        <text x={26} y={114} fontFamily="DM Mono, monospace" fontSize={13} fontWeight={700} fill="#5B6770" letterSpacing={2}>BROKER SIDE</text>
        <text x={1031} y={114} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={13} fontWeight={700} fill="#9298A0" letterSpacing={2}>CLIENT SIDE</text>

        {/* shared source */}
        <rect x={24} y={276} width={150} height={88} rx={12} fill="#082B3E" />
        <text x={99} y={310} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={17} fontWeight={700} fill="#fff">Source topic</text>
        <text x={99} y={332} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#9CC6F5" letterSpacing={1}>35,000 MSG/S</text>
        <path d="M174 300 C 210 300, 210 150, 250 150" fill="none" stroke="#D7CDC3" strokeWidth={2} />
        <path d="M174 340 C 210 340, 210 490, 250 490" fill="none" stroke="#D7CDC3" strokeWidth={2} />

        {/* ── PRIOR-ART belt (top) ───────────────────────────── */}
        <text x={250} y={104} className="cmp-side-lbl cmp-side-lbl--good" textAnchor="start">JMS · SOLACE · MQTT — FILTER AT THE BROKER</text>
        <rect x={250} y={132} width={690} height={36} rx={18} fill="#F4F2EE" stroke="#D7CDC3" strokeWidth={1.5} />
        <line className="cmpc-belt" x1={250} y1={150} x2={940} y2={150} stroke="#D7CDC3" strokeWidth={2} strokeDasharray="10 12" />
        <g className="cmpc-arm">
          <line x1={620} y1={120} x2={620} y2={150} stroke="#0070D9" strokeWidth={4} strokeLinecap="round" />
          <circle cx={620} cy={120} r={6} fill="#0070D9" />
        </g>
        <text x={640} y={122} textAnchor="start" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#0E4A8E" letterSpacing={1}>FILTER</text>
        <text x={620} y={212} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#697586" letterSpacing={0.8}>unwanted flicked off</text>
        <rect x={956} y={120} width={150} height={64} rx={10} fill="#fff" stroke="#202939" strokeWidth={2} />
        <text x={1031} y={149} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={17} fontWeight={700} fill="#202939">Consumer</text>
        <text x={1031} y={169} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#0E4A8E" letterSpacing={1}>KEEPS UP · LAG 0</text>
        {CMP_TILES.map((t, i) => (
          <ConveyorTile key={`c-top${i}`} anchor={[268, 150]} d={t.d} wanted={t.w}
            cls={`cmp-tile cmpc ${t.w ? 'cmpc-pass' : 'cmpc-flick'}`} />
        ))}

        {/* ── KAFKA belt (bottom) ────────────────────────────── */}
        <text x={250} y={444} className="cmp-side-lbl cmp-side-lbl--kafka" textAnchor="start">APACHE KAFKA — NO BROKER FILTER · KAFKA-6020</text>
        <rect x={250} y={472} width={690} height={36} rx={18} fill="#F4F2EE" stroke="#D7CDC3" strokeWidth={1.5} />
        <line className="cmpc-belt" x1={250} y1={490} x2={940} y2={490} stroke="#D7CDC3" strokeWidth={2} strokeDasharray="10 12" />
        <rect x={956} y={430} width={150} height={150} rx={10} fill="#fff" stroke="#202939" strokeWidth={2} />
        <text x={1031} y={418} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={17} fontWeight={700} fill="#202939">Consumer</text>
        <clipPath id="cmpc-inbox"><rect x={958} y={432} width={146} height={146} rx={9} /></clipPath>
        <g clipPath="url(#cmpc-inbox)">
          <rect x={960} y={580} width={142} height={150} className="cmpc-overflow" fill="#5B6770" />
        </g>
        <text x={1031} y={556} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#fff" letterSpacing={1}>LAG ↑</text>
        {CMP_TILES.map((t, i) => (
          <ConveyorTile key={`c-bot${i}`} anchor={[268, 490]} d={t.d} wanted={t.w} cls="cmp-tile cmpc cmpc-ride" />
        ))}

        {/* wasted client-side work — OUTSIDE the broker barrier, at the consumer */}
        <text x={1031} y={606} textAnchor="middle" fontFamily="Funnel Display, sans-serif" fontSize={27} fontWeight={600} fill="#8B3F58" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtC(wasted)}</text>
        <text x={1031} y={623} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fill="#8B3F58" letterSpacing={0.8}>DESERIALIZED, THEN DISCARDED</text>
        <text x={1031} y={635} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9} fill="#8B3F58" letterSpacing={0.8}>~98% OF EVERY BATCH</text>
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   "So we built one" section · slides 8–13 (ported from Claude Design
   handoff). Layout + motion classes (.built-*) live in styles.css scoped
   to [data-deck="broker-side-filter-for-kafka"]. Each public component
   is wrapped with useSlideActivation so animations restart on re-entry.
   ══════════════════════════════════════════════════════════════════════ */

/* ── Shared code-block renderer ──────────────────────────────────────────
   lines: [{ t: text, hl?: bool, note?: string, c?: 'cm'|'kw' }]. One line can
   carry hl (the "only new line" highlight) + a note rendered to its right. */
type CodeLine = { t?: string; hl?: boolean; note?: string; c?: 'cm' | 'kw' };
function CodeBlock({
  lines,
  size = 'md',
  className = '',
}: {
  lines: ReadonlyArray<CodeLine>;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <pre className={`built-code built-code--${size} ${className}`}>
      {lines.map((ln, i) => (
        <div key={i} className={`built-cl${ln.hl ? ' built-cl--hl' : ''}${ln.c ? ` built-cl--${ln.c}` : ''}`}>
          <span className="built-cl-txt">{ln.t || ' '}</span>
          {ln.note ? <span className="built-cl-note">{ln.note}</span> : null}
        </div>
      ))}
    </pre>
  );
}

/* Badge helper from the source handoff was only consumed by TrinityCodeHero
   and TwoComponents (both variants we skipped) — dropped to keep the
   typecheck clean. The CSS rules remain in styles.css for forward use. */

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 8 · "So we built one." — YOUR APP trinity (solo variant).
   ════════════════════════════════════════════════════════════════════════ */

const APP_CODE: ReadonlyArray<CodeLine> = [
  { t: 'import io.streamnative.filter.client.kafka.FilterableKafkaConsumers;', c: 'kw' },
  { t: '...' },
  { t: '' },
  { t: 'Properties props = new Properties();' },
  { t: 'props.put(ConsumerConfig.BOOTSTRAP_SERVERS_CONFIG, "localhost:9092");' },
  { t: 'props.put(ConsumerConfig.GROUP_ID_CONFIG, "my-group");' },
  { t: 'props.put(ConsumerConfig.KEY_DESERIALIZER_CLASS_CONFIG,   StringDeserializer.class.getName());' },
  { t: 'props.put(ConsumerConfig.VALUE_DESERIALIZER_CLASS_CONFIG, StringDeserializer.class.getName());' },
  { t: 'props.put(ConsumerConfig.AUTO_OFFSET_RESET_CONFIG, "earliest");' },
  { t: '' },
  { t: 'Consumer<String, String> consumer = FilterableKafkaConsumers' },
  { t: '        .<String, String>newConsumer(props)' },
  { t: '        .topic("trades")                       // Kafka topic name' },
  { t: '        .selector("region = \'US\'")             // ← the only new line vs vanilla Kafka' },
  { t: '        .subscribe();' },
  { t: '' },
  { t: '// Ordinary Kafka poll loop — you only ever receive records where region = \'US\'.', c: 'cm' },
  { t: 'while (true) {' },
  { t: '    ConsumerRecords<String, String> records = consumer.poll(Duration.ofMillis(500));' },
  { t: '    for (ConsumerRecord<String, String> record : records) {' },
  { t: '        System.out.printf("key=%s  value=%s%n", record.key(), record.value());' },
  { t: '    }' },
  { t: '}' },
];

function TrinityCardsInner() {
  return (
    <div className="built-trinity built-trinity--solo">
      <div className="built-tcards">
        {/* YOUR APP */}
        <article className="built-tcard built-tcard--app built-tcard--code">
          <header className="built-tcard-head">
            <span className="built-tcard-ey">YOUR&nbsp;APP</span>
          </header>
          <p className="built-tcard-lede">
            Same Kafka client library, same <code>Properties</code>, same <code>poll()</code> loop.
            One added line — filtering happens at the broker, so you receive only matches.
          </p>
          <CodeBlock lines={APP_CODE} size="lg" />
        </article>
      </div>
    </div>
  );
}

export function TrinityCards() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <TrinityCardsInner key={activation} />
    </div>
  );
}

/* Shared by the slide-9 TwoComponents delegation diagram (Component A · the
   Native Kafka SDK). The five method names the SDK delegates straight
   through to the underlying real KafkaConsumer. */
const DELEGATED: ReadonlyArray<string> = ['poll', 'commit', 'seek', 'assignment', 'metrics'];

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 11 · Dynamic filters (the SetSelector story).
   ════════════════════════════════════════════════════════════════════════ */

const DYN_CODE: ReadonlyArray<CodeLine> = [
  { t: 'public final class IntradayWatchlist {' },
  { t: '' },
  { t: '    public static void main(String[] args) throws Exception {' },
  { t: '        Consumer<String, String> c = FilterableKafkaConsumers' },
  { t: '                .<String, String>newConsumer(props)' },
  { t: '                .topic("ticks")                                   // raw market-data feed' },
  { t: '                .selector("__key__ IN (\'AAPL\',\'MSFT\',\'GOOGL\')")   // 09:30 ET — opening basket' },
  { t: '                .subscribe();' },
  { t: '' },
  { t: '        // The poll loop never changes — it just keeps receiving whatever the', c: 'cm' },
  { t: '        // *current* selector matches. The broker swaps the filter underneath it.', c: 'cm' },
  { t: '        Runnable pollLoop = () -> {' },
  { t: '            while (!Thread.currentThread().isInterrupted()) {' },
  { t: '                ConsumerRecords<String, String> records = c.poll(Duration.ofMillis(500));' },
  { t: '                for (ConsumerRecord<String, String> r : records) {' },
  { t: '                    System.out.printf("%s = %s%n", r.key(), r.value());' },
  { t: '                }' },
  { t: '            }' },
  { t: '        };' },
  { t: '        Thread worker = new Thread(pollLoop, "poll-loop");' },
  { t: '        worker.start();' },
  { t: '' },
  { t: '        // ── Dynamic filter changes — same consumer, same queryId, no re-subscribe ──', c: 'cm' },
  { t: '' },
  { t: '        // 14:00 ET — trader adds defensives mid-session. The broker hot-reloads the', c: 'cm' },
  { t: '        // filter in place; the poll loop above immediately starts seeing the new names.', c: 'cm' },
  { t: '        ((FilterControl) c).setSelector(' },
  { t: '                "__key__ IN (\'AAPL\',\'MSFT\',\'GOOGL\',\'XLU\',\'TLT\',\'GLD\')");' },
  { t: '' },
  { t: '        // 15:55 ET — narrow back down into the close.', c: 'cm' },
  { t: '        ((FilterControl) c).setSelector(' },
  { t: '                "__key__ IN (\'AAPL\',\'MSFT\',\'GOOGL\')");' },
  { t: '' },
  { t: '        // End of day — close() retires the filter query (and, with newConsumer(props),', c: 'cm' },
  { t: '        // closes the owned control-plane Session).', c: 'cm' },
  { t: '        worker.interrupt();' },
  { t: '        c.close();' },
  { t: '    }' },
  { t: '}' },
];

type FlowStage = { k: string; s: string };
const FLOW_STAGES: ReadonlyArray<FlowStage> = [
  { k: 'setSelector(…)', s: 'SDK call' },
  { k: 'control topic', s: 'QueryMessage published' },
  { k: 'orchestrator', s: 'routes a MODIFY' },
  { k: 'TableView', s: 'per-filter hot-reload' },
];

const DYN_USES: ReadonlyArray<string> = [
  'Intraday watchlist changes — trader edits, no app restart',
  'Incident response — mute a noisy key in seconds',
  'A/B-test selector variants on the same private topic',
  'Scheduled time-of-day shifts via cron',
];

function DynamicFlowInner() {
  return (
    <div className="built-dyn">
      <article className="built-tcard built-tcard--app built-tcard--code built-dyn-card">
        <header className="built-tcard-head">
          <span className="built-tcard-ey">ONE LINE CHANGES THE FILTER — NO RESTART</span>
        </header>
        <CodeBlock lines={DYN_CODE} size="lg" />
      </article>

      <div className="built-dyn-right">
        <div className="built-flow">
          <span className="built-tcard-ey">UNDER THE HOOD</span>
          <div className="built-flow-stages">
            {FLOW_STAGES.map((st, i) => (
              <Fragment key={st.k}>
                <div className="built-stage" style={{ animationDelay: `${600 + i * 700}ms` }}>
                  <span className="built-stage-k">{st.k}</span>
                  <span className="built-stage-s">{st.s}</span>
                </div>
                {i < FLOW_STAGES.length - 1 ? (
                  <span className="built-stage-arrow" style={{ animationDelay: `${950 + i * 700}ms` }}>→</span>
                ) : null}
              </Fragment>
            ))}
          </div>
          <div className="built-flow-live">
            <span className="built-flow-num">~1s</span>
            <span className="built-flow-cap">typical end-to-end propagation — next message matches the new selector</span>
          </div>
        </div>

        <div className="built-uses">
          <span className="built-tcard-ey">REAL-WORLD USES</span>
          <ul>
            {DYN_USES.map((u) => <li key={u}>{u}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function DynamicFlow() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <DynamicFlowInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 13 · Trajectory + community ask (horizontal-rail variant).
   ════════════════════════════════════════════════════════════════════════ */

type MilestoneState = 'done' | 'today' | 'roadmap';
type Milestone = { n: string; label: string; year?: string; sub: string; state: MilestoneState };

const MILESTONES: ReadonlyArray<Milestone> = [
  { n: '01', label: 'Pulsar-only', year: '2025', sub: 'selector-filter as a Pulsar Function. Pulsar SDK clients on both ends. Where the work started.', state: 'done' },
  { n: '02', label: 'Kafka-compatible', year: '2026', sub: 'Native Kafka producers + consumers on the wire. KSN broker terminates the Kafka protocol.', state: 'done' },
  { n: '03', label: 'Native Kafka SDKs', sub: 'Java + Python drop-in SDKs shipping now. Consumer returns a real KafkaConsumer; producer stays vanilla. Running live in the demo.', state: 'today' },
  { n: '04', label: 'Full native Apache Kafka', sub: 'Apache Kafka brokers, no KSN intermediary. Same selector-filter engine, ported.', state: 'roadmap' },
  { n: '05', label: 'Apache Kafka KIP', sub: 'Upstream the SDK + a pluggable backend interface so Flink, Kafka Streams, or any engine can implement the filtering surface.', state: 'roadmap' },
];

function TrajectoryTimelineInner() {
  return (
    <div className="built-traj built-traj--rail">
      <div className="built-rail">
        <div className="built-rail-solid" />
        <div className="built-rail-dashed" />
        {MILESTONES.map((m, i) => (
          <div key={m.n} className={`built-rnode built-rnode--${m.state} built-rnode--${i % 2 === 0 ? 'above' : 'below'}${i === 0 ? ' built-rnode--first' : ''}${i === MILESTONES.length - 1 ? ' built-rnode--last' : ''}`}
            style={{ left: `calc(${i * 25}% ${(2 - i) >= 0 ? '+' : '-'} ${Math.abs(2 - i) / 4} * clamp(160px, 17vw, 230px) - 75px)` }}>
            <div className="built-rdot" style={{ animationDelay: `${500 + i * 320}ms` }} />
            {m.state === 'today' ? <div className="built-rtoday">TODAY</div> : null}
            <div className="built-rcard" style={{ animationDelay: `${680 + i * 320}ms` }}>
              <div className="built-rcard-top">
                <span className="built-rcard-n">{m.n}</span>
                {m.year ? <span className="built-rcard-year">{m.year}</span> : null}
                {m.state === 'roadmap' ? <span className="built-rchip">ROADMAP</span> : null}
              </div>
              <p className="built-rcard-label">{m.label}</p>
              <p className="built-rcard-sub">{m.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TrajectoryTimeline() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <TrajectoryTimelineInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   "Two key components" — the solution at a glance.
   Component A · Native Kafka SDK (reuses the .built-deleg delegation
   diagram from AdoptionPanel). Component B · Filtering engine: a
   three-tier stack (Kafka-API-compatible broker → horizontally-scaling
   engine pods → durable control plane). Section opener for what's behind
   the wire.
   ════════════════════════════════════════════════════════════════════════ */
function TwoComponentsInner() {
  return (
    <div className="built-two">
      {/* COMPONENT A · Native Kafka SDK */}
      <article className="built-two-col">
        <header className="built-two-head">
          <span className="built-two-ey">COMPONENT&nbsp;A · CLIENT</span>
          <h3 className="built-two-title">Native Kafka SDK</h3>
          <p className="built-two-desc">Returns a <strong>real <code>KafkaConsumer</code></strong>.</p>
        </header>
        <div className="built-deleg">
          <div className="built-deleg-iface">
            <span className="built-deleg-type">Consumer&lt;K, V&gt;</span>
            <span className="built-deleg-cap">what your code holds — a real KafkaConsumer</span>
          </div>
          <div className="built-deleg-methods">
            {DELEGATED.map((m, i) => (
              <span key={m} className="built-deleg-pill" style={{ animationDelay: `${500 + i * 110}ms` }}>{m}<span className="built-deleg-arrow">→</span></span>
            ))}
          </div>
          <div className="built-deleg-real">delegates straight through to a real <strong>KafkaConsumer</strong></div>
          <div className="built-deleg-fc">
            <span className="built-deleg-fctag">FilterControl</span>
            <span className="built-deleg-fccap">the only surface that knows about filtering — cast for it when you need it, ignore it when you don&apos;t</span>
          </div>
        </div>
      </article>

      {/* COMPONENT B · Filtering engine */}
      <article className="built-two-col">
        <header className="built-two-head">
          <span className="built-two-ey">COMPONENT&nbsp;B · SERVER</span>
          <h3 className="built-two-title">Filtering engine</h3>
          <p className="built-two-desc">Horizontally scalable, <strong>alongside a Kafka-API-compatible broker</strong> — backed by a durable control plane.</p>
        </header>
        <div className="built-eng">
          <div className="built-eng-tier built-eng-tier--broker">
            <span className="built-eng-tier-lbl">Kafka-API-compatible broker</span>
            <span className="built-eng-tier-cap">terminates the wire protocol — your Kafka clients connect unchanged</span>
          </div>
          <div className="built-eng-flow"><span>filtering delegated to the engine</span></div>
          <div className="built-eng-tier built-eng-tier--engine">
            <div className="built-eng-tierhead">
              <span className="built-eng-tier-lbl">Filtering engine</span>
              <span className="built-eng-scale">scales horizontally →</span>
            </div>
            <div className="built-eng-podrow">
              <span className="built-eng-pod">filter-0</span>
              <span className="built-eng-pod built-eng-pod--scale" style={{ animationDelay: '700ms' }}>filter-1</span>
              <span className="built-eng-pod built-eng-pod--scale" style={{ animationDelay: '900ms' }}>filter-2</span>
              <span className="built-eng-pod built-eng-pod--add built-eng-pod--scale" style={{ animationDelay: '1100ms' }}>+&nbsp;N</span>
            </div>
          </div>
          <div className="built-eng-flow"><span>backed by</span></div>
          <div className="built-eng-tier built-eng-tier--cp">
            <span className="built-eng-cp-tag">Durable control plane</span>
            <span className="built-eng-tier-cap"><strong>Resilient</strong> — control topic · orchestrator · per-filter TableView. Survives restarts; filters resume where they left off.</span>
          </div>
        </div>
      </article>
    </div>
  );
}

export function TwoComponents() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <TwoComponentsInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   ARCHITECTURE SECTION (slides 15–19)
   ────────────────────────────────────────────────────────────────────────
   Locked visual dictionary, ported from the Claude Design exploration:
   indigo cylinders + hexagons = control plane; teal cylinders = data plane;
   the filter funnel (amber accent on white) straddles the boundary. The
   `Locator` mini-map keeps audience-orientation across the zoom-in slides.

   Each public component wraps an Inner body and re-mounts on slide
   re-entry via useSlideActivation (matching the existing pattern).
   ════════════════════════════════════════════════════════════════════════ */

const ARCH_PAL = {
  indigo: { fill: '#D1E5FB', top: '#B9D7FA', stroke: '#09315F', ink: '#09315F' },
  teal:   { fill: '#C1CFD7', top: '#93A6B0', stroke: '#082B3D', ink: '#082B3D' },
  slate:  { fill: '#E9E2DA', top: '#D7CDC3', stroke: '#364152', ink: '#364152' },
  amber:  { fill: '#EFD49C', top: '#E0B968', stroke: '#8A5210', ink: '#6E3F0C' },
  rose:   { fill: '#F3D3DE', top: '#E4A6BC', stroke: '#8B3F58', ink: '#6E2F45' },
} as const;
type ArchTone = keyof typeof ARCH_PAL;
const ARCH_FLOW = { data: '#365463', control: '#0E4A8E', lossy: '#8B3F58' } as const;

/* ── Topic cylinder ─────────────────────────────────────────────────── */
function ArchCylinder({
  cx, top, w, h, tone = 'teal', dim = false, label, sub, labelInk, strong = false,
}: {
  cx: number; top: number; w: number; h: number;
  tone?: ArchTone; dim?: boolean; label?: string; sub?: string;
  labelInk?: string; strong?: boolean;
}) {
  const p = ARCH_PAL[tone];
  const x = cx - w / 2;
  const ry = Math.min(w * 0.13, 13);
  const body = `M${x},${top + ry} V${top + h - ry} A${w / 2},${ry} 0 0 0 ${x + w},${top + h - ry} V${top + ry}`;
  return (
    <g opacity={dim ? 0.26 : 1}>
      <path d={body} fill={p.fill} stroke={p.stroke} strokeWidth={strong ? 2.6 : 2} />
      <ellipse cx={cx} cy={top + ry} rx={w / 2} ry={ry} fill={p.top} stroke={p.stroke} strokeWidth={strong ? 2.6 : 2} />
      {label ? (
        <text x={cx} y={top + h / 2 + 5} textAnchor="middle" fontFamily="DM Mono, monospace"
          fontSize={13} fontWeight={600} fill={labelInk || p.ink} letterSpacing={0.3}>{label}</text>
      ) : null}
      {sub ? (
        <text x={cx} y={top + h + 18} textAnchor="middle" fontFamily="DM Mono, monospace"
          fontSize={10.5} fill="#697586" letterSpacing={1.2}>{sub}</text>
      ) : null}
    </g>
  );
}

/* ── Filter-function FUNNEL ─────────────────────────────────────────── */
function ArchFunnel({
  cx, cy, r, dim = false, label, spin = false, rot = 0,
}: {
  cx: number; cy: number; r: number;
  dim?: boolean; label?: string; spin?: boolean; rot?: number;
}) {
  const stroke = '#0E4A8E', fill = '#D1E5FB', rim = '#EAF2FD', ink = '#09315F';
  const topY = cy - r * 0.96, botY = cy + r * 0.98;
  const neckY = cy + r * 0.16;
  const tw = r, nw = r * 0.20, rimRy = r * 0.16;
  const body = `M${cx - tw},${topY} L${cx + tw},${topY} L${cx + nw},${neckY} L${cx + nw},${botY} L${cx - nw},${botY} L${cx - nw},${neckY} Z`;
  const labelY = rot ? cy + r + 22 : botY + r * 0.6;
  const gR = r * 0.44, toothW = gR * 0.32, toothH = gR * 0.46;
  const gy = topY - gR * 0.58;
  const teeth: React.ReactNode[] = [];
  for (let i = 0; i < 8; i++) {
    teeth.push(
      <rect key={i} x={-toothW / 2} y={-(gR + toothH * 0.5)} width={toothW} height={toothH} rx={2}
        fill={fill} stroke={stroke} strokeWidth={2} transform={`rotate(${45 * i})`} />
    );
  }
  return (
    <g opacity={dim ? 0.3 : 1}>
      <g transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}>
        <g transform={`translate(${cx} ${gy})`}>
          <g className={spin ? 'arch-cogspin' : ''} style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
            {teeth}
            <circle r={gR} fill={fill} stroke={stroke} strokeWidth={2.6} />
            <circle r={gR * 0.46} fill="#fff" stroke={stroke} strokeWidth={2.2} />
          </g>
        </g>
        <path d={body} fill={fill} stroke={stroke} strokeWidth={2.6} strokeLinejoin="round" />
        <ellipse cx={cx} cy={topY} rx={tw} ry={rimRy} fill={rim} stroke={stroke} strokeWidth={2.6} />
      </g>
      {label ? <text x={cx} y={labelY} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={ink} letterSpacing={0.8}>{label}</text> : null}
    </g>
  );
}

/* ── Orchestrator CONTROLLER (node + reconcile ring) ─────────────────── */
function ArchOrchestrator({
  cx, cy, r, dim = false, label = 'orchestrator', sub,
}: {
  cx: number; cy: number; r: number;
  dim?: boolean; label?: string; sub?: string;
}) {
  const p = ARCH_PAL.indigo;
  const s = r * 0.82;
  const ring = r + 13;
  const sat: ReadonlyArray<readonly [number, number]> = [-90, 30, 150].map((d) => {
    const a = (Math.PI / 180) * d;
    return [cx + s * 0.5 * Math.cos(a), cy + s * 0.5 * Math.sin(a)] as const;
  });
  const ha = (Math.PI / 180) * -38;
  const hx = cx + ring * Math.cos(ha), hy = cy + ring * Math.sin(ha);
  const hsz = 9;
  return (
    <g opacity={dim ? 0.3 : 1}>
      <circle cx={cx} cy={cy} r={ring} fill="none" stroke={p.stroke} strokeWidth={2.2} className="arch-fc" />
      <polygon points={`0,0 ${-hsz},${-hsz * 0.58} ${-hsz},${hsz * 0.58}`} fill={p.stroke} transform={`translate(${hx} ${hy}) rotate(${-38 + 90})`} />
      <rect x={cx - s} y={cy - s} width={s * 2} height={s * 2} rx={s * 0.36} fill={p.fill} stroke={p.stroke} strokeWidth={2.6} />
      {sat.map(([sx, sy], i) => <line key={'l' + i} x1={cx} y1={cy - 1} x2={sx} y2={sy} stroke={p.stroke} strokeWidth={1.6} opacity={0.7} />)}
      {sat.map(([sx, sy], i) => <circle key={'s' + i} cx={sx} cy={sy} r={s * 0.16} fill={p.fill} stroke={p.stroke} strokeWidth={1.6} />)}
      <circle cx={cx} cy={cy - 1} r={s * 0.20} fill={p.stroke} />
      {label ? <text x={cx} y={cy + ring + 18} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fontWeight={700} fill={p.ink} letterSpacing={0.4}>{label}</text> : null}
      {sub ? <text x={cx} y={cy + ring + 34} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fill={p.stroke} letterSpacing={1}>{sub}</text> : null}
    </g>
  );
}

/* ── Client glyph (SDK or consumer) ─────────────────────────────────── */
function ArchClient({
  x, y, w = 116, h = 78, dim = false, label, sub, badge,
}: {
  x: number; y: number; w?: number; h?: number;
  dim?: boolean; label?: string; sub?: string; badge?: string;
}) {
  const p = ARCH_PAL.slate;
  return (
    <g opacity={dim ? 0.28 : 1}>
      <rect x={x} y={y} width={w} height={h} rx={11} fill="#fff" stroke={p.stroke} strokeWidth={2.2} />
      <rect x={x} y={y} width={w} height={20} rx={11} fill={p.fill} stroke="none" />
      <rect x={x} y={y + 12} width={w} height={9} fill={p.fill} stroke="none" />
      <line x1={x} y1={y + 21} x2={x + w} y2={y + 21} stroke={p.stroke} strokeWidth={1.4} />
      {[0, 1, 2].map((i) => <circle key={i} cx={x + 13 + i * 11} cy={y + 11} r={2.6} fill={p.stroke} opacity={0.55} />)}
      {label ? <text x={x + w / 2} y={y + 46} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={16} fontWeight={700} fill={p.ink}>{label}</text> : null}
      {sub ? <text x={x + w / 2} y={y + 65} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fill="#697586" letterSpacing={0.8}>{sub}</text> : null}
      {badge ? <text x={x + w / 2} y={y + h + 17} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={600} fill={p.stroke} letterSpacing={1.2}>{badge}</text> : null}
    </g>
  );
}

/* ── Vertical band tab (CONTROL PLANE / DATA PLANE) ─────────────────── */
function ArchBandTab({
  x, y, w, h, tone, title, kicker,
}: {
  x: number; y: number; w: number; h: number;
  tone: ArchTone; title: string; kicker?: string;
}) {
  const p = ARCH_PAL[tone];
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={10} fill="none" stroke={p.stroke} strokeWidth={1.4} strokeDasharray="2 5" opacity={0.55} />
      <g transform={`translate(${x + 17} ${y + h / 2}) rotate(-90)`}>
        <text textAnchor="middle" y={-3} fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={p.ink} letterSpacing={2.2}>{title}</text>
        {kicker ? <text textAnchor="middle" y={13} fontFamily="DM Mono, monospace" fontSize={9} fill={p.stroke} letterSpacing={1.4} opacity={0.8}>{kicker}</text> : null}
      </g>
    </g>
  );
}

/* Shared CSS injected once per SVG. Marching dashes = live flow. */
const ARCH_MAP_CSS = `
  .arch-fd { stroke-dasharray: 12 7; animation: arch-march-d 0.95s linear infinite; }
  .arch-fc { stroke-dasharray: 3 6;  animation: arch-march-c 1.15s linear infinite; }
  .arch-fl { stroke-dasharray: 6 6;  animation: arch-march-c 1.1s linear infinite; }
  @keyframes arch-march-d { to { stroke-dashoffset: -19; } }
  @keyframes arch-march-c { to { stroke-dashoffset: -9; } }
`;

function archAng(x1: number, y1: number, x2: number, y2: number): number {
  return Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
}

function ArchHead({ x, y, ang, color, size = 9 }: {
  x: number; y: number; ang: number; color: string; size?: number;
}) {
  return <polygon points={`0,0 ${-size},${-size * 0.58} ${-size},${size * 0.58}`} fill={color} transform={`translate(${x} ${y}) rotate(${ang})`} />;
}

type ArchFlowKind = 'data' | 'ctrl' | 'lossy';
function ArchFlow({
  x1, y1, x2, y2, kind = 'data', head = true, dim = false,
}: {
  x1: number; y1: number; x2: number; y2: number;
  kind?: ArchFlowKind; head?: boolean; dim?: boolean;
}) {
  const c = kind === 'data' ? ARCH_FLOW.data : kind === 'ctrl' ? ARCH_FLOW.control : ARCH_FLOW.lossy;
  const w = kind === 'data' ? 3.2 : 2;
  const cls = kind === 'data' ? 'arch-fd' : kind === 'ctrl' ? 'arch-fc' : 'arch-fl';
  return (
    <g opacity={dim ? 0.3 : 1}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={w} className={cls} strokeLinecap="round" fill="none" />
      {head ? <ArchHead x={x2} y={y2} ang={archAng(x1, y1, x2, y2)} color={c} size={kind === 'data' ? 9 : 7.5} /> : null}
    </g>
  );
}

/* ── Locator mini-map ────────────────────────────────────────────────
   A fixed schematic of the whole system, rendered on every zoom-in slide.
   focus: 'control' | 'filter' | 'output' | 'scale' lights one region. */
type ArchFocus = 'control' | 'filter' | 'output' | 'scale';
const ARCH_LOC_W = 392;
const ARCH_LOC_H = 108;
function ArchLocator({ focus }: { focus: ArchFocus }) {
  const op = (on: boolean | number) => (on ? 1 : 0.28);
  const onCtrl = focus === 'control';
  const onFilter = focus === 'filter';
  const onOut = focus === 'output';
  const onScale = focus === 'scale';
  const hiTable = {
    control: { x: 92, y: 8, w: 168, h: 44 },
    filter:  { x: 168, y: 8, w: 70, h: 92 },
    output:  { x: 250, y: 56, w: 118, h: 44 },
    scale:   { x: 168, y: 8, w: 216, h: 92 },
  } as const;
  const hi = hiTable[focus];
  const dot = (cx: number, cy: number, tone: ArchTone, on: number) => (
    <g opacity={on}>
      <circle cx={cx} cy={cy} r={8} fill={ARCH_PAL[tone].top} stroke={ARCH_PAL[tone].stroke} strokeWidth={2.2} />
    </g>
  );
  const box = (x: number, y: number, tone: ArchTone, on: number) => (
    <rect x={x} y={y} width={26} height={22} rx={4} fill={ARCH_PAL[tone].fill} stroke={ARCH_PAL[tone].stroke} strokeWidth={2} opacity={on} />
  );
  return (
    <svg data-archloc="1" style={{ display: 'block', height: 'clamp(70px, 10vh, 104px)', width: 'auto' }} viewBox={`0 0 ${ARCH_LOC_W} ${ARCH_LOC_H}`} role="img" aria-label="Locator: which region of the architecture this slide zooms into.">
      {hi ? <rect x={hi.x} y={hi.y} width={hi.w} height={hi.h} rx={9} fill="rgba(10,111,210,0.09)" stroke="#0E4A8E" strokeWidth={2} strokeDasharray="5 3" /> : null}
      <text x={6} y={28} fontFamily="DM Mono, monospace" fontSize={9} fontWeight={700} fill={ARCH_PAL.indigo.ink} letterSpacing={1}>CTRL</text>
      <text x={6} y={96} fontFamily="DM Mono, monospace" fontSize={9} fontWeight={700} fill={ARCH_PAL.teal.ink} letterSpacing={1}>DATA</text>
      <line x1={44} y1={54} x2={ARCH_LOC_W - 8} y2={54} stroke="#C9C2B8" strokeWidth={1.4} strokeDasharray="2 5" />
      {box(44, 19, 'slate', onCtrl || onScale ? 0.6 : op(onCtrl))}
      {dot(112, 30, 'indigo', op(onCtrl))}
      <g opacity={op(onCtrl)}>
        <circle cx={164} cy={30} r={14} fill="none" stroke={ARCH_PAL.indigo.stroke} strokeWidth={1.6} strokeDasharray="4 3" />
        <rect x={156} y={22} width={16} height={16} rx={4} fill={ARCH_PAL.indigo.fill} stroke={ARCH_PAL.indigo.stroke} strokeWidth={2} />
        <circle cx={164} cy={30} r={2.4} fill={ARCH_PAL.indigo.stroke} />
      </g>
      {dot(214, 30, 'indigo', op(onCtrl))}
      {dot(232, 30, 'indigo', op(onCtrl))}
      {dot(64, 78, 'teal', onFilter || onScale ? 1 : 0.5)}
      <g transform="translate(202 60)" opacity={onFilter || onScale ? 1 : 0.34}>
        <path d="M-14,-12 L14,-12 L4,3 L4,13 L-4,13 L-4,3 Z" fill={ARCH_PAL.amber.fill} stroke={ARCH_PAL.amber.stroke} strokeWidth={2} strokeLinejoin="round" />
        <ellipse cx={0} cy={-12} rx={14} ry={2.8} fill={ARCH_PAL.amber.top} stroke={ARCH_PAL.amber.stroke} strokeWidth={2} />
      </g>
      {dot(266, 78, 'teal', onOut || onScale ? 1 : 0.48)}
      {dot(284, 78, 'teal', onOut || onScale ? 1 : 0.48)}
      {box(332, 67, 'slate', onOut || onScale ? 0.9 : 0.42)}
    </svg>
  );
}

function ArchLocStrip({ focus, k, children }: {
  focus: ArchFocus; k: string; children: React.ReactNode;
}) {
  return (
    <div className="arch-loc">
      <ArchLocator focus={focus} />
      <div className="arch-loc-cap">
        <span className="arch-loc-k">{k}</span>
        <span className="arch-loc-s">{children}</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 15 · MasterMapSequence — variant A, 8-step animated sequence
   ════════════════════════════════════════════════════════════════════════ */

type SeqTone = 'ctrl' | 'data';
const SEQ_STEPS: ReadonlyArray<{ n: number; tone: SeqTone; t: React.ReactNode }> = [
  { n: 1, tone: 'ctrl', t: <>The consumer app calls the <strong>SDK</strong>, which publishes a <code>filter_request</code> to the <strong>filter-requests</strong> topic.</> },
  { n: 2, tone: 'ctrl', t: <>The <strong>orchestrator</strong> reads the new request off the control topic.</> },
  { n: 3, tone: 'ctrl', t: <>It spawns a <strong>filter function</strong> and a dedicated <strong>filtered topic</strong> — both at once, if they don&rsquo;t already exist.</> },
  { n: 4, tone: 'ctrl', t: <>The orchestrator returns the <strong>filtered-topic name</strong> back through the SDK.</> },
  { n: 5, tone: 'ctrl', t: <>The app&rsquo;s <strong>KafkaConsumer</strong> connects straight to the filtered topic — not the source. <strong>Transparent</strong> to the consumer.</> },
  { n: 6, tone: 'data', t: <>The <strong>source stream</strong> flows into the filter function.</> },
  { n: 7, tone: 'data', t: <>Matches <strong>pass through</strong> to the filtered topic; everything else is <strong>dropped</strong>.</> },
  { n: 8, tone: 'data', t: <>The consumer receives <strong>only the messages it asked for</strong> — from its own topic.</> },
];

const SEQ_CSS = `
  .seq-mc { stroke-dasharray: 3 6;  animation: seq-mc 1.05s linear infinite; }
  .seq-md { stroke-dasharray: 13 8; animation: seq-md .85s linear infinite; }
  @keyframes seq-mc { to { stroke-dashoffset: -9; } }
  @keyframes seq-md { to { stroke-dashoffset: -21; } }
  @keyframes seq-pop { 0% { transform: scale(.5); } 60% { transform: scale(1.07); } 100% { transform: scale(1); } }
  .seq-pop { animation: seq-pop .55s cubic-bezier(.2,.8,.3,1) both; transform-box: fill-box; transform-origin: center; }
  .seq-hot { animation: seq-hot 1.25s ease-in-out infinite alternate; }
  @keyframes seq-hot { from { opacity: .6; } to { opacity: 1; } }
`;

function archFlowColor(kind: ArchFlowKind): string {
  return kind === 'data' ? ARCH_FLOW.data : kind === 'ctrl' ? ARCH_FLOW.control : ARCH_FLOW.lossy;
}

function SArrow({
  step, my, kind = 'ctrl', live = false, x1, y1, x2, y2, w,
}: {
  step: number; my: number; kind?: ArchFlowKind; live?: boolean;
  x1: number; y1: number; x2: number; y2: number; w?: number;
}) {
  if (step < my) return null;
  const c = archFlowColor(kind);
  const hot = step === my;
  const showFlow = hot || (live && step >= my);
  return (
    <g style={{ opacity: (live || hot) ? 1 : 0.16, transition: 'opacity .45s ease' }}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={w || (kind === 'data' ? 3.4 : 2.4)} strokeLinecap="round" className={showFlow ? (kind === 'data' ? 'seq-md' : 'seq-mc') : ''} />
      <ArchHead x={x2} y={y2} ang={archAng(x1, y1, x2, y2)} color={c} size={kind === 'data' ? 10 : 8} />
    </g>
  );
}
function SPath({
  step, my, kind = 'ctrl', live = false, d, hx, hy, ha, w,
}: {
  step: number; my: number; kind?: ArchFlowKind; live?: boolean;
  d: string; hx: number; hy: number; ha: number; w?: number;
}) {
  if (step < my) return null;
  const c = archFlowColor(kind);
  const hot = step === my;
  const showFlow = hot || (live && step >= my);
  return (
    <g style={{ opacity: (live || hot) ? 1 : 0.16, transition: 'opacity .45s ease' }}>
      <path d={d} fill="none" stroke={c} strokeWidth={w || (kind === 'data' ? 3.4 : 2.4)} strokeLinecap="round" strokeLinejoin="round" className={showFlow ? (kind === 'data' ? 'seq-md' : 'seq-mc') : ''} />
      <ArchHead x={hx} y={hy} ang={ha} color={c} size={kind === 'data' ? 10 : 8} />
    </g>
  );
}
function SBadge({ step, my, tone, x, y }: {
  step: number; my: number; tone: SeqTone; x: number; y: number;
}) {
  if (step < my) return null;
  const p = tone === 'data' ? ARCH_PAL.teal : ARCH_PAL.indigo;
  const active = step === my;
  return (
    <g style={{ opacity: active ? 1 : 0.5, transition: 'opacity .4s' }}>
      <circle cx={x} cy={y} r={active ? 15 : 11.5} fill="#fff" stroke={p.stroke} strokeWidth={active ? 3 : 2} className={active ? 'seq-hot' : ''} />
      <text x={x} y={y + (active ? 5.5 : 4.5)} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={active ? 15 : 12} fontWeight={700} fill={p.ink}>{my}</text>
    </g>
  );
}
function SLabel({
  step, my, x, y, anchor = 'middle', tone = 'ctrl', children,
}: {
  step: number; my: number; x: number; y: number;
  anchor?: 'start' | 'middle' | 'end'; tone?: ArchFlowKind; children: React.ReactNode;
}) {
  if (step < my) return null;
  const p = tone === 'data' ? ARCH_PAL.teal : tone === 'lossy' ? ARCH_PAL.rose : ARCH_PAL.indigo;
  return <text x={x} y={y} textAnchor={anchor} fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={p.ink} letterSpacing={0.4} style={{ opacity: step === my ? 1 : 0.5, transition: 'opacity .4s' }}>{children}</text>;
}

function MasterMapSequenceInner() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (s: number) => {
      const delay = s === 0 ? 3200 : s >= 8 ? 14400 : 6200;
      timer = setTimeout(() => {
        if (cancelled) return;
        const next = s >= 8 ? 0 : s + 1;
        setStep(next); schedule(next);
      }, delay);
    };
    schedule(step);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  const jump = (n: number) => { setPaused(true); setStep(n); };
  const active = step >= 1 ? SEQ_STEPS[Math.min(7, step - 1)] : null;
  const capTone: SeqTone = active ? active.tone : 'ctrl';

  return (
    <div className="arch-seq">
      <div className="arch-seq-svg">
        <svg data-arch="1" style={{ display: 'block' }} viewBox="0 0 1400 470" role="img"
          aria-label="Setup sequence for one consumer's filter, in eight steps.">
          <style>{SEQ_CSS + ARCH_MAP_CSS}</style>

          {/* bands + boundary */}
          <rect x={288} y={26} width={1088} height={178} rx={16} fill="#EAF2FD" stroke="#B9D7FA" strokeWidth={1.5} />
          <rect x={288} y={222} width={1088} height={230} rx={16} fill="#E6F4F0" stroke="#93A6B0" strokeWidth={1.5} />
          <line x1={292} y1={213} x2={1372} y2={213} stroke="#BAAFA5" strokeWidth={1.6} strokeDasharray="2 8" />
          <ArchBandTab x={298} y={36} w={24} h={150} tone="indigo" title="CONTROL PLANE" kicker="SETS IT UP" />
          <ArchBandTab x={298} y={232} w={24} h={210} tone="teal" title="DATA PLANE" kicker="STREAMS IT" />

          {/* client box (left, spans both planes) */}
          <g>
            <rect x={40} y={118} width={212} height={236} rx={16} fill="#fff" stroke={ARCH_PAL.slate.stroke} strokeWidth={2.6} />
            <path d="M40 134 a16 16 0 0 1 16 -16 H236 a16 16 0 0 1 16 16 V148 H40 Z" fill={ARCH_PAL.slate.fill} />
            <line x1={40} y1={148} x2={252} y2={148} stroke={ARCH_PAL.slate.stroke} strokeWidth={1.6} />
            <text x={146} y={139} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={ARCH_PAL.slate.ink} letterSpacing={1.4}>CONSUMER APPLICATION</text>
            <rect x={62} y={160} width={170} height={78} rx={10} fill={ARCH_PAL.slate.fill} stroke={ARCH_PAL.slate.stroke} strokeWidth={2} />
            <text x={147} y={193} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={18} fontWeight={700} fill={ARCH_PAL.slate.ink}>SDK</text>
            <text x={147} y={215} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fill="#697586" letterSpacing={0.4}>control-plane API</text>
            <rect x={62} y={252} width={170} height={80} rx={10} fill="#fff" stroke={ARCH_PAL.slate.stroke} strokeWidth={2} />
            <text x={147} y={283} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={15} fontWeight={700} fill={ARCH_PAL.slate.ink}>KafkaConsumer</text>
            <text x={147} y={305} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9} fill="#697586" letterSpacing={0.4}>reads filtered stream</text>
          </g>

          {/* pre-existing infra */}
          <ArchCylinder cx={500} top={66} w={150} h={88} tone="indigo" label="filter-requests" sub="ONE FRONT DOOR" />
          <ArchOrchestrator cx={880} cy={108} r={44} label="orchestrator" sub="RECONCILES" />
          <ArchCylinder cx={1180} top={262} w={132} h={150} tone="teal" label="source topic" strong />

          {/* slots before creation (step < 3) */}
          {step < 3 ? (
            <g style={{ opacity: 0.5 }}>
              <rect x={806} y={284} width={108} height={92} rx={14} fill="none" stroke={ARCH_PAL.amber.stroke} strokeWidth={2} strokeDasharray="5 5" />
              <text x={860} y={326} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={ARCH_PAL.amber.stroke}>filter fn</text>
              <text x={860} y={344} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fill={ARCH_PAL.amber.stroke} letterSpacing={1}>created at 3</text>
              <rect x={395} y={276} width={150} height={128} rx={14} fill="none" stroke={ARCH_PAL.teal.stroke} strokeWidth={2} strokeDasharray="5 5" />
              <text x={470} y={335} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={ARCH_PAL.teal.ink}>filtered topic</text>
              <text x={470} y={353} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fill={ARCH_PAL.teal.ink} letterSpacing={1}>created at 3</text>
            </g>
          ) : null}

          {/* created glyphs (step >= 3, pop in) */}
          {step >= 3 ? (
            <g className="seq-pop"><ArchFunnel cx={860} cy={330} r={52} spin rot={90} label="filter function" /></g>
          ) : null}
          {step >= 3 ? (
            <g className="seq-pop"><ArchCylinder cx={470} top={276} w={150} h={128} tone="teal" label="filtered topic" strong /></g>
          ) : null}

          {/* SETUP arrows */}
          <SArrow step={step} my={1} kind="ctrl" x1={232} y1={198} x2={423} y2={118} />
          <SLabel step={step} my={1} x={360} y={176} tone="ctrl">filter_request</SLabel>
          <SBadge step={step} my={1} tone="ctrl" x={360} y={146} />
          <SArrow step={step} my={2} kind="ctrl" x1={577} y1={110} x2={822} y2={110} />
          <SLabel step={step} my={2} x={700} y={134} tone="ctrl">reads request</SLabel>
          <SBadge step={step} my={2} tone="ctrl" x={700} y={96} />
          <SArrow step={step} my={3} kind="ctrl" x1={878} y1={166} x2={862} y2={280} />
          <SArrow step={step} my={3} kind="ctrl" x1={842} y1={138} x2={512} y2={280} />
          <SLabel step={step} my={3} x={690} y={212} tone="ctrl">creates both</SLabel>
          <SBadge step={step} my={3} tone="ctrl" x={690} y={186} />
          <SPath step={step} my={4} kind="ctrl" d="M 880 49 C 720 4 360 4 196 64 C 178 76 160 96 150 116" hx={150} hy={118} ha={108} />
          <SLabel step={step} my={4} x={512} y={42} tone="ctrl">returns topic name</SLabel>
          <SBadge step={step} my={4} tone="ctrl" x={512} y={16} />
          <SArrow step={step} my={5} kind="ctrl" x1={252} y1={288} x2={391} y2={320} />
          <SBadge step={step} my={5} tone="ctrl" x={360} y={312} />

          {/* DATA arrows */}
          <SArrow step={step} my={6} kind="data" live x1={1114} y1={335} x2={918} y2={330} />
          <SBadge step={step} my={6} tone="data" x={1016} y={318} />
          <SArrow step={step} my={7} kind="data" live x1={806} y1={332} x2={552} y2={340} />
          <SBadge step={step} my={7} tone="data" x={690} y={320} />
          {step >= 7 ? (
            <g style={{ opacity: step === 7 ? 1 : 0.55, transition: 'opacity .4s' }}>
              <line x1={792} y1={348} x2={706} y2={398} stroke={ARCH_FLOW.lossy} strokeWidth={2.2} strokeLinecap="round" className="seq-mc" />
              <ArchHead x={706} y={398} ang={archAng(792, 348, 706, 398)} color={ARCH_FLOW.lossy} size={8} />
              <text x={690} y={414} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill={ARCH_PAL.rose.ink}>dropped</text>
            </g>
          ) : null}
          <SArrow step={step} my={8} kind="data" live x1={391} y1={360} x2={252} y2={316} />
          <SBadge step={step} my={8} tone="data" x={360} y={350} />
        </svg>
      </div>

      <div className="arch-seq-rail">
        <div className="arch-seq-dots">
          <span className="arch-seq-phase">SETUP &#9656;</span>
          {SEQ_STEPS.map((s, i) => (
            <Fragment key={s.n}>
              {i === 5 ? <span className="arch-seq-phase">STREAMING &#9656;</span> : null}
              <button
                className={`arch-seq-dot arch-seq-dot--${s.tone}${step === s.n ? ' is-active' : ''}${step > s.n ? ' is-done' : ''}`}
                onClick={() => jump(s.n)} aria-label={`Step ${s.n}`}>{s.n}</button>
            </Fragment>
          ))}
          <button className="arch-seq-play" onClick={() => setPaused((p) => !p)}>
            <span className={`glyph glyph--${paused ? 'play' : 'pause'}`} aria-hidden="true" />{paused ? 'Play' : 'Pause'}
          </button>
        </div>
        <div className={`arch-seq-cap arch-seq-cap--${capTone}`}>
          {active ? (
            <><span className="arch-seq-cap-n">Step {active.n}</span><span className="arch-seq-cap-t">{active.t}</span></>
          ) : (
            <span className="arch-seq-cap-t" style={{ color: 'var(--sn-grey-5)' }}>One consumer requests a filter — watch the eight-step setup, then the steady-state stream.</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function MasterMapSequence() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <MasterMapSequenceInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 16 · ControlPlaneRing — variant A, reconcile ring as hero
   ════════════════════════════════════════════════════════════════════════ */

type JsonLine = { h: string; hl?: boolean };

function ArchJson({ lines }: { lines: ReadonlyArray<JsonLine> }) {
  return (
    <pre className="arch-code arch-code--xs">
      {lines.map((l, i) => <div key={i} className={`arch-cl${l.hl ? ' arch-cl--hl' : ''}`} dangerouslySetInnerHTML={{ __html: l.h }} />)}
    </pre>
  );
}

const REGISTER_JSON: ReadonlyArray<JsonLine> = [
  { h: '{' },
  { h: '  <span class="k">"command"</span>:    <span class="s">"REGISTER"</span>,' },
  { h: '  <span class="k">"queryId"</span>:    <span class="s">"a3f1&hellip;9c"</span>,  <span class="c">// UUIDv4</span>' },
  { h: '  <span class="k">"inputTopic"</span>: <span class="s">"trades"</span>,' },
  { h: '  <span class="k">"selector"</span>:   <span class="s">"region = \'US\'"</span>' },
  { h: '}' },
];
const MODIFY_JSON: ReadonlyArray<JsonLine> = [
  { h: '{' },
  { h: '  <span class="k">"command"</span>:  <span class="n">"MODIFY"</span>,' },
  { h: '  <span class="k">"queryId"</span>:  <span class="s">"a3f1&hellip;9c"</span>,  <span class="c">// SAME id</span>', hl: true },
  { h: '  <span class="k">"selector"</span>: <span class="s">"region IN (\'US\',\'EU\')"</span>', hl: true },
  { h: '}' },
];

const COMMANDS: ReadonlyArray<{ n: string; d: React.ReactNode; hb?: boolean }> = [
  { n: 'REGISTER', d: <>New filter, keyed by <code>queryId</code>. Tri-state idempotent &mdash; broker redelivery is safe.</> },
  { n: 'MODIFY', d: <>In-place selector swap on the same <code>queryId</code>. No re-subscribe.</> },
  { n: 'UNREGISTER', d: <>Retire a filter. Unknown query = silent OK.</> },
  { n: 'HEARTBEAT', d: <>Liveness. Key arbitrary; body&rsquo;s <code>queryIds</code> is authoritative.</>, hb: true },
];

function ControlWire() {
  return (
    <div className="arch-cp-col">
      <div className="arch-swap">
        <span className="arch-ey">ONE FRONT DOOR &middot; FOUR LIFECYCLE COMMANDS</span>
        <div className="arch-swap-cards">
          <div className="arch-swap-card">
            <div className="arch-jsonhead"><span className="arch-jsontag arch-jsontag--reg">REGISTER</span> open a filter</div>
            <ArchJson lines={REGISTER_JSON} />
          </div>
          <div className="arch-swap-card">
            <div className="arch-jsonhead"><span className="arch-jsontag arch-jsontag--mod">MODIFY</span> same query</div>
            <ArchJson lines={MODIFY_JSON} />
          </div>
        </div>
        <div className="arch-swap-note">
          <span className="arch-swap-arrow">&rarr;</span>
          <span className="arch-swap-txt">Same <code>queryId</code> &rarr; <b>in-place selector swap</b>. No re-subscribe, no new output topic &mdash; the mechanism behind the <code>setSelector</code> demo.</span>
        </div>
      </div>

      <div className="arch-cmds">
        {COMMANDS.map((c) => (
          <div key={c.n} className={`arch-cmd${c.hb ? ' arch-cmd--hb' : ''}`}>
            <span className="arch-cmd-n">{c.n}</span>
            <span className="arch-cmd-d">{c.d}</span>
          </div>
        ))}
      </div>

      <div className="arch-choice arch-choice--teal">
        <span className="arch-choice-k">Why TableView &middot; durable state</span>
        <span className="arch-choice-b">A compacted topic materialized as an in-memory map &mdash; <strong>it ships with the platform</strong>, no separate cluster, no extra operational surface.</span>
        <span className="arch-alts">Alternatives considered: <em>Kafka KTables &middot; embedded RocksDB &middot; an external KV store</em>. The pattern is portable; TableView is one valid pick.</span>
      </div>
    </div>
  );
}

function TableViewCard() {
  return (
    <div className="arch-tv">
      <div className="arch-tv-head">
        <span className="arch-tv-ttl">TableView &middot; durable state</span>
        <span className="arch-tv-tag">REHYDRATES ON RESTART</span>
      </div>
      <div className="arch-tv-row">
        <span className="arch-tv-k">t:&lt;inputTopic&gt;</span>
        <span className="arch-tv-v">&rarr; <b>RegistryEntry</b> &middot; activeQueryIds &middot; idleSince &middot; deserializers &middot; clientType</span>
      </div>
      <div className="arch-tv-row">
        <span className="arch-tv-k">q:&lt;queryId&gt;</span>
        <span className="arch-tv-v">&rarr; <b>QueryEntry</b> &middot; lastSeen &middot; back-ref to input topic</span>
      </div>
    </div>
  );
}

function Sweeper() {
  return (
    <div className="arch-sweep">
      <span className="arch-sweep-g" aria-hidden="true" />
      <span className="arch-sweep-t"><b>IdleFilterSweeper</b> &mdash; GCs filters whose activeQueryIds emptied past their idle TTL; reaps consumers that never called close().</span>
    </div>
  );
}

const RING_CSS = `
  .arch-ring-rot { stroke-dasharray: 7 10; animation: arch-ring-march 1.4s linear infinite; }
  @keyframes arch-ring-march { to { stroke-dashoffset: -17; } }
  .arch-ring-node { animation: arch-ring-pulse 1.8s ease-in-out infinite alternate; }
  @keyframes arch-ring-pulse { from { opacity: 0.6; } to { opacity: 1; } }
`;
function ReconcileRing() {
  const cx = 280, cy = 248, r = 166;
  const nodes: ReadonlyArray<{ a: number; t: string; s: string; anchor: 'start' | 'middle' | 'end'; labelX?: number }> = [
    { a: -90, t: 'OBSERVE', s: 'read actual', anchor: 'middle' },
    { a: 30, t: 'DIFF', s: 'desired vs actual', anchor: 'start', labelX: 450 },
    { a: 150, t: 'ACT', s: 'spawn / modify / GC', anchor: 'end', labelX: 110 },
  ];
  const pt = (a: number, rad: number): [number, number] => [cx + rad * Math.cos(a * Math.PI / 180), cy + rad * Math.sin(a * Math.PI / 180)];
  const [hx, hy] = pt(-40, r);
  return (
    <svg data-arch="1" style={{ display: 'block' }} viewBox="-120 0 800 432" role="img"
      aria-label="A reconciliation control loop: observe actual state, diff against desired, act — repeating forever, like a Kubernetes controller.">
      <style>{RING_CSS}</style>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#B9D7FA" strokeWidth={13} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0E4A8E" strokeWidth={4.5} className="arch-ring-rot" />
      <ArchHead x={hx} y={hy} ang={-40 + 90} color="#0E4A8E" size={14} />
      <text x={cx} y={cy + 1} textAnchor="middle" fontFamily="Funnel Display, sans-serif" fontWeight={600} fontSize={30} fill="#09315F">RECONCILE</text>
      <text x={cx} y={cy + 24} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={13} fill="#0E4A8E" letterSpacing={0.5}>converge desired &#8635; actual</text>
      {nodes.map((n, i) => {
        const [nx, ny] = pt(n.a, r);
        const [ex, ey] = pt(n.a, r + 22);
        const [lxR, lyR] = pt(n.a, r + 54);
        const lx = n.labelX != null ? n.labelX : lxR;
        const ly = lyR;
        return (
          <g key={i} className="arch-ring-node" style={{ animationDelay: `${i * 0.5}s` }}>
            <line x1={nx} y1={ny} x2={ex} y2={ey} stroke="#0E4A8E" strokeWidth={1.6} />
            <circle cx={nx} cy={ny} r={11} fill="#fff" stroke="#0E4A8E" strokeWidth={3.4} />
            <circle cx={nx} cy={ny} r={4.6} fill="#0E4A8E" />
            <text x={lx} y={ly - 6} textAnchor={n.anchor} fontFamily="DM Mono, monospace" fontSize={24} fontWeight={700} fill="#09315F" letterSpacing={1}>{n.t}</text>
            <text x={lx} y={ly + 17} textAnchor={n.anchor} fontFamily="Funnel Sans, sans-serif" fontSize={21} fill="#5B6770">{n.s}</text>
          </g>
        );
      })}
    </svg>
  );
}

function ControlPlaneRingInner() {
  return (
    <div className="arch-zoom">
      <div className="arch-z-main">
        <div className="arch-cp">
          <ControlWire />
          <div className="arch-cp-col">
            <span className="arch-ey">LIKE A KUBERNETES CONTROLLER</span>
            <div className="arch-cp-diagram arch-cp-diagram--ring">
              <ReconcileRing />
            </div>
            <TableViewCard />
            <Sweeper />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ControlPlaneRing() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <ControlPlaneRingInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 17 · ProcessorSlide — filter function, N filters != N deserializes
   ════════════════════════════════════════════════════════════════════════ */

const FF_CSS = `
  .ff-fan-lit { stroke-dasharray: 12 7; animation: ff-march 0.9s linear infinite; }
  @keyframes ff-march { to { stroke-dashoffset: -19; } }
`;

function FFMsg({ d, match = false }: { d: string; match?: boolean }) {
  return (
    <g opacity="0">
      <rect x={-17} y={-12} width={34} height={24} rx={5} fill="#0070D9" stroke="#0C5FB7" strokeWidth={1.4} />
      <text y={5} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#fff">msg</text>
      <animateMotion dur="3s" repeatCount="indefinite" calcMode="linear"
        path={d}
        keyTimes={match ? '0;0.4;0.85;1' : '0;0.4;1'}
        keyPoints={match ? '0;0.335;1;1' : '0;1;1'} />
      <animate attributeName="opacity" dur="3s" repeatCount="indefinite"
        values={match ? '0;1;1;1;0' : '0;1;1;0;0'}
        keyTimes={match ? '0;0.08;0.4;0.85;1' : '0;0.1;0.4;0.62;1'} />
    </g>
  );
}

const FF_QUERIES: ReadonlyArray<{ q: string; sel: string; outY: number }> = [
  { q: 'q1', sel: "region = 'US'", outY: 96 },
  { q: 'q2', sel: "tier = 'PREMIUM'", outY: 184 },
  { q: 'q3', sel: "region IN ('US','EU')", outY: 272 },
  { q: 'q4', sel: "__key__ = 'X9'", outY: 360 },
];
const FF_MESSAGES: ReadonlyArray<ReadonlyArray<string>> = [
  ['q1', 'q3'],
  ['q4'],
  ['q2'],
  ['q1', 'q4'],
];

function FilterFunction() {
  const tableX = 700, tableY = 78, tableW = 250, rowH = 52;
  const outX = 1142;
  const [mi, setMi] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMi((m) => (m + 1) % FF_MESSAGES.length), 3000);
    return () => clearInterval(id);
  }, []);
  const hit = FF_MESSAGES[mi];
  const isMatch = (q: string) => hit.indexOf(q) !== -1;
  return (
    <svg data-arch="1" style={{ display: 'block' }} viewBox="0 0 1400 430" role="img"
      aria-label="One filter function per source topic. Each message is deserialized exactly once, evaluated against every registered selector from one control-topic TableView keyed by queryId, and re-published only to the matching filters' output topics.">
      <style>{FF_CSS + ARCH_MAP_CSS}</style>

      <ArchCylinder cx={108} top={150} w={120} h={138} tone="teal" label="source topic" sub="ONE PER WORKER" strong />

      <ArchFlow x1={172} y1={219} x2={300} y2={216} kind="data" />

      <rect x={320} y={166} width={16} height={96} rx={4} fill="#0070D9" />
      <text x={328} y={154} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#0E4A8E" letterSpacing={1}>DESERIALIZE</text>
      <text x={328} y={282} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={700} fill="#0E4A8E" letterSpacing={1}>ONCE PER MESSAGE</text>
      <ArchFlow x1={344} y1={214} x2={404} y2={214} kind="data" />

      <ArchFunnel cx={470} cy={214} r={46} spin />

      {FF_QUERIES.map((s, i) => {
        const rowY = tableY + 60 + i * rowH + 21;
        return <ArchFlow key={s.q} x1={532} y1={214} x2={694} y2={rowY} kind="ctrl" />;
      })}
      <text x={611} y={120} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#0E4A8E" letterSpacing={1}>
        <tspan x={611} dy={0}>APPLIED IN</tspan>
        <tspan x={611} dy={13}>PARALLEL</tspan>
      </text>

      <rect x={tableX} y={tableY} width={tableW} height={300} rx={12} fill="#082B3E" stroke="#082B3D" strokeWidth={2} />
      <text x={tableX + 16} y={tableY + 26} fontFamily="DM Mono, monospace" fontSize={12} fontWeight={700} fill="#fff" letterSpacing={0.4}>control-topic TableView</text>
      <text x={tableX + 16} y={tableY + 44} fontFamily="DM Mono, monospace" fontSize={9.5} fill="#7AD8CB" letterSpacing={1}>KEYED BY queryId</text>
      {FF_QUERIES.map((s, i) => {
        const m = isMatch(s.q);
        const y = tableY + 60 + i * rowH;
        return (
          <g key={s.q} style={{ transition: 'opacity .3s' }}>
            <rect x={tableX + 12} y={y} width={tableW - 24} height={rowH - 10} rx={6}
              fill={m ? 'rgba(122,216,203,0.18)' : 'rgba(255,255,255,0.05)'}
              stroke={m ? '#7AD8CB' : 'rgba(255,255,255,0.16)'} strokeWidth={1.4} />
            <text x={tableX + 24} y={y + 18} fontFamily="DM Mono, monospace" fontSize={12} fontWeight={700} fill={m ? '#7AD8CB' : 'rgba(255,255,255,0.5)'}>{s.q}</text>
            <text x={tableX + 24} y={y + 33} fontFamily="DM Mono, monospace" fontSize={10.5} fill={m ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.42)'}>{s.sel}</text>
            <text x={tableX + tableW - 22} y={y + 27} textAnchor="end" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={700} fill={m ? '#7AD8CB' : 'rgba(255,255,255,0.32)'}>{m ? 'MATCH' : '—'}</text>
          </g>
        );
      })}

      {FF_QUERIES.map((s, i) => {
        const m = isMatch(s.q);
        const y = tableY + 60 + i * rowH + 21;
        const oy = s.outY + 36;
        if (m) {
          return <line key={s.q} x1={tableX + tableW} y1={y} x2={outX} y2={oy} stroke={ARCH_FLOW.data} strokeWidth={3} className="ff-fan-lit" strokeLinecap="round" />;
        }
        return <line key={s.q} x1={tableX + tableW} y1={y} x2={outX} y2={oy} stroke="#C9C2B8" strokeWidth={1.6} strokeDasharray="3 6" opacity={0.6} />;
      })}

      {FF_QUERIES.map((s) => {
        const m = isMatch(s.q);
        return <ArchCylinder key={s.q} cx={outX + 56} top={s.outY} w={108} h={70} tone={m ? 'teal' : 'slate'} dim={!m} label={`out·${s.q[1]}`} />;
      })}
      <text x={outX + 56} y={18} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fontWeight={700} fill="#082B3D" letterSpacing={1}>ONLY MATCHES GET A COPY</text>

      <g key={mi}>
        {FF_QUERIES.map((s, i) => {
          const rowY = tableY + 60 + i * rowH + 21;
          if (isMatch(s.q)) {
            const outEntry = s.outY + 36;
            return <FFMsg key={s.q} match d={`M532,214 L730,${rowY} L950,${rowY} L${outX},${outEntry}`} />;
          }
          return <FFMsg key={s.q} d={`M532,214 L730,${rowY}`} />;
        })}
      </g>
    </svg>
  );
}

function ProcessorSlideInner() {
  return (
    <div className="arch-zoom">
      <div className="arch-ff">
        <div className="arch-ff-diagram"><FilterFunction /></div>
        <div className="arch-fcards">
          <div className="arch-fcard arch-fcard--stat">
            <span className="arch-fcard-k">The efficiency core</span>
            <div className="arch-stat-line"><span className="arch-stat-n">100 filters</span><span className="arch-stat-eq">= 1 deserialize / msg</span></div>
            <span className="arch-fcard-b"><strong>N filters on a topic &ne; N deserializations.</strong> A hundred filters watching one topic cost one deserialize per message, not a hundred.</span>
          </div>
          <div className="arch-fcard arch-fcard--plain">
            <span className="arch-fcard-k">Why Pulsar Functions</span>
            <span className="arch-fcard-b">Single-instance lifecycle semantics, in-platform deploy &mdash; the runtime for the worker.</span>
            <span className="arch-alts">Alternatives: <em>Flink &middot; Kafka Streams</em>. The same architecture runs on either; Pulsar Functions is the pick here.</span>
          </div>
          <div className="arch-fcard arch-fcard--plain">
            <span className="arch-fcard-k">Hot-reload</span>
            <span className="arch-fcard-b">A <code>MODIFY</code> on the control topic updates the TableView; the selector swap is live within one round-trip &mdash; <strong>~1s end-to-end</strong>.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProcessorSlide() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <ProcessorSlideInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 18 · OutputTopicsSlide — per-filter output topics
   ════════════════════════════════════════════════════════════════════════ */

function OutputTopicsDiagram() {
  return (
    <svg data-arch="1" style={{ display: 'block' }} viewBox="0 0 1400 400" role="img"
      aria-label="Each filter writes to its own dedicated output topic. A consumer can crash and reconnect to resume its persisted stream, retention TTL is tuned per consumer, and reconnecting consumers can seek to latest to skip the backlog.">
      <style>{ARCH_MAP_CSS}</style>

      <ArchFunnel cx={88} cy={186} r={40} spin label="filter fn" />
      <ArchFlow x1={130} y1={186} x2={300} y2={188} kind="data" />

      <path d="M306 108 V96 H470 V108" fill="none" stroke="#082B3D" strokeWidth={2} />
      <text x={388} y={84} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#082B3D" letterSpacing={0.8}>RETENTION TTL &middot; TUNED HERE</text>

      <ArchCylinder cx={388} top={120} w={158} h={150} tone="teal" label="filtered-<uuid>" sub="DEDICATED · PER FILTER" strong />

      <line x1={324} y1={300} x2={448} y2={300} stroke="#365463" strokeWidth={2.4} strokeDasharray="6 5" className="arch-fd" />
      <ArchHead x={448} y={300} ang={0} color="#365463" size={9} />
      <text x={388} y={322} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#365463" letterSpacing={0.6}>SEEK TO LATEST &middot; skip backlog</text>

      <ArchClient x={560} y={58} w={132} h={72} dim label="consumer" badge="CRASHED" />
      <ArchFlow x1={520} y1={188} x2={648} y2={224} kind="data" />
      <ArchClient x={636} y={206} w={132} h={72} label="consumer" sub="RESUMES" />
      <path d="M626 96 q 70 70 18 108" fill="none" stroke="#8B3F58" strokeWidth={2} strokeDasharray="5 5" />
      <ArchHead x={644} y={204} ang={110} color="#8B3F58" size={8} />
      <text x={726} y={150} fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#8B3F58" letterSpacing={0.4}>crash &rarr;</text>
      <text x={726} y={166} fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#8B3F58" letterSpacing={0.4}>reconnect</text>

      <line x1={902} y1={40} x2={902} y2={360} stroke="#D7CDC3" strokeWidth={1.5} strokeDasharray="2 7" />

      <g opacity={0.82}>
        <rect x={936} y={52} width={440} height={300} rx={12} fill="#FBF4F6" stroke="#D8B6C2" strokeWidth={1.4} strokeDasharray="4 4" />
        <text x={1156} y={82} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fontWeight={700} fill="#8B3F58" letterSpacing={1.4}>WHAT WE AVOID &middot; SHARED TOPIC</text>
        <path d="M1010 130 V112 H1130 V130" fill="none" stroke="#8B3F58" strokeWidth={1.8} />
        <text x={1070} y={104} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fontWeight={700} fill="#8B3F58" letterSpacing={0.4}>ONE TTL FOR ALL</text>
        <ArchCylinder cx={1070} top={140} w={120} h={120} tone="rose" label="shared" />
        {[150, 230, 310].map((y, i) => (
          <g key={i}>
            <line x1={1130} y1={200} x2={1262} y2={y + 22} stroke="#B98098" strokeWidth={1.6} strokeDasharray="3 4" />
            <rect x={1262} y={y} width={92} height={44} rx={7} fill="#fff" stroke="#B98098" strokeWidth={1.6} />
            <text x={1308} y={y + 27} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={11} fill="#8B3F58">cons&middot;{i + 1}</text>
          </g>
        ))}
        <text x={1136} y={344} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={12.5} fill="#8B3F58">lowest-common-denominator retention</text>
      </g>
    </svg>
  );
}

const PAYOFFS: ReadonlyArray<{ n: string; h: string; b: React.ReactNode }> = [
  { n: '01', h: 'Resilience', b: <>Consumer crashes? The filtered stream <strong>persisted independently</strong>. Reconnect just resumes the subscription.</> },
  { n: '02', h: 'Per-filter retention', b: <>TTL tuned to that one consumer&rsquo;s needs &mdash; <strong>no lowest-common-denominator</strong> retention on a shared topic.</> },
  { n: '03', h: 'Resume-from-latest', b: <>Only the newest data matters? Reconnecting consumers <strong>seek to latest</strong> &mdash; no backlog churn to reach &ldquo;now.&rdquo;</> },
];

function OutputTopicsSlideInner() {
  return (
    <div className="arch-zoom">
      <div className="arch-ff">
        <div className="arch-ff-diagram"><OutputTopicsDiagram /></div>
        <div className="arch-fcards arch-fcards--even">
          {PAYOFFS.map((p) => (
            <div key={p.n} className="arch-payoff">
              <div className="arch-payoff-top"><span className="arch-payoff-n">{p.n}</span><span className="arch-payoff-h">{p.h}</span></div>
              <span className="arch-payoff-b">{p.b}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function OutputTopicsSlide() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <OutputTopicsSlideInner key={activation} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SLIDE 19 · ScalingThree — Key_Shared × HPA + honest trade-offs
   ════════════════════════════════════════════════════════════════════════ */

const KSA_OWN: ReadonlyArray<{ s: string; f: string; ink: string }> = [
  { s: '#365463', f: '#C1CFD7', ink: '#082B3D' },
  { s: '#0E4A8E', f: '#D1E5FB', ink: '#09315F' },
  { s: '#B5781F', f: '#EFD49C', ink: '#7A5210' },
];
type KsaPhase = {
  instances: ReadonlyArray<{ keys: ReadonlyArray<number>; cpu: number }>;
  spawn?: number;
  breach?: boolean;
  note: React.ReactNode;
};
const KSA_PHASES: ReadonlyArray<KsaPhase> = [
  { instances: [{ keys: [0, 1, 2, 3, 4, 5], cpu: 80 }], breach: true,
    note: <><strong>One instance</strong> owns the whole key space &mdash; CPU climbs to <strong>80%</strong>.</> },
  { instances: [{ keys: [0, 1, 2], cpu: 42 }, { keys: [3, 4, 5], cpu: 40 }], spawn: 1,
    note: <><strong>HPA adds filter-fn 2</strong> &mdash; a new consumer on the same KEY_SHARED sub takes <strong>half the keys</strong>.</> },
  { instances: [{ keys: [0, 1, 2], cpu: 80 }, { keys: [3, 4, 5], cpu: 80 }], breach: true,
    note: <>Load keeps rising &mdash; <strong>both instances reach 80%</strong>.</> },
  { instances: [{ keys: [0, 1], cpu: 54 }, { keys: [2, 3], cpu: 52 }, { keys: [4, 5], cpu: 50 }], spawn: 2,
    note: <><strong>HPA adds filter-fn 3</strong> &mdash; the key space <strong>rebalances three ways</strong>.</> },
];
const KSA_CSS = `
  .ksa-cpu { transition: transform .9s ease, fill .4s ease; }
  .ksa-pop { animation: ksa-pop .55s cubic-bezier(.2,.8,.3,1) both; transform-box: fill-box; transform-origin: center bottom; }
  @keyframes ksa-pop { 0% { transform: scale(.4); } 60% { transform: scale(1.08); } 100% { transform: scale(1); } }
  .ksa-flash { animation: ksa-flash 1s ease-in-out infinite; }
  @keyframes ksa-flash { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
  .ksa-key { transition: fill .5s ease, stroke .5s ease; }
`;
function KeySharedAutoscale({ phase }: { phase: number }) {
  const ph = KSA_PHASES[phase];
  const insts = ph.instances;
  const NKEYS = 6;
  const keyW = 80, keyGap = 7, keyX0 = 70;
  const keyX = (i: number) => keyX0 + i * (keyW + keyGap);
  const keyCX = (i: number) => keyX(i) + keyW / 2;
  const ownerOf = (k: number) => insts.findIndex((it) => it.keys.indexOf(k) !== -1);
  const instX = insts.length === 1 ? [330] : insts.length === 2 ? [212, 448] : [150, 330, 510];
  const gW = 104, gY = 372;
  return (
    <svg data-arch="1" style={{ display: 'block' }} viewBox="0 0 660 460" role="img"
      aria-label="One KEY_SHARED subscription feeding filter-function instances. As load rises the HPA spawns new instances that join the same subscription and split the key space; ordering is preserved per key.">
      <style>{KSA_CSS}</style>

      <text x={70} y={30} fontFamily="DM Mono, monospace" fontSize={12.5} fontWeight={700} fill="#082B3D" letterSpacing={1.4}>KEY_SHARED SUBSCRIPTION</text>
      <text x={590} y={30} textAnchor="end" fontFamily="Funnel Sans, sans-serif" fontSize={12.5} fill="#697586">ordering preserved per key</text>
      <rect x={56} y={44} width={548} height={60} rx={12} fill="#F4F8FC" stroke="#9FB6C9" strokeWidth={1.6} />
      {Array.from({ length: NKEYS }).map((_, i) => {
        const o = ownerOf(i);
        const c = KSA_OWN[o] || { f: '#ECECEC', s: '#BBB', ink: '#888' };
        return (
          <g key={i}>
            <rect className="ksa-key" x={keyX(i)} y={56} width={keyW} height={36} rx={7} fill={c.f} stroke={c.s} strokeWidth={2} />
            <text x={keyCX(i)} y={79} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fontWeight={600} fill={c.ink}>key&middot;{i + 1}</text>
          </g>
        );
      })}

      {Array.from({ length: NKEYS }).map((_, i) => {
        const o = ownerOf(i);
        const c = KSA_OWN[o];
        return <line key={i} className="ksa-key" x1={keyCX(i)} y1={92} x2={instX[o]} y2={266} stroke={c.s} strokeWidth={1.8} opacity={0.6} />;
      })}

      {insts.map((it, idx) => {
        const x = instX[idx];
        const c = KSA_OWN[idx];
        const breach = it.cpu >= 80;
        const gx = x - gW / 2;
        return (
          <g key={idx} className={ph.spawn === idx ? 'ksa-pop' : ''}>
            <ArchFunnel cx={x} cy={300} r={32} spin />
            <rect x={x - 50} y={338} width={100} height={22} rx={11} fill={c.f} stroke={c.s} strokeWidth={1.6} />
            <text x={x} y={353} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fontWeight={700} fill={c.ink}>filter-fn {idx + 1}</text>
            <rect x={gx} y={gY} width={gW} height={16} rx={5} fill="#EEEAE3" stroke="#CFC7BB" strokeWidth={1.2} />
            <rect className="ksa-cpu" x={gx} y={gY} width={gW} height={16} rx={5}
              fill={breach ? '#D9A23E' : '#1C8A5B'}
              style={{ transform: `scaleX(${it.cpu / 100})`, transformBox: 'fill-box', transformOrigin: 'left center' }} />
            <line x1={gx + gW * 0.8} y1={gY - 4} x2={gx + gW * 0.8} y2={gY + 20} stroke="#8B3F58" strokeWidth={1.6} strokeDasharray="2 2" />
            <text x={x} y={gY + 36} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={12} fontWeight={700} fill={breach ? '#B5781F' : '#5B6770'}>CPU {it.cpu}%</text>
            {breach ? (
              <g className="ksa-flash">
                <rect x={x - 52} y={262} width={104} height={22} rx={11} fill="#FBE7CC" stroke="#B5781F" strokeWidth={1.4} />
                <text x={x} y={277} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#B5781F" letterSpacing={0.6}>80% &rarr; HPA</text>
              </g>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

function TradeOffs() {
  return (
    <div className="arch-trade">
      <div className="arch-tband arch-tband--keep">
        <span className="arch-badge arch-badge--keep"><span className="arch-badge-glyph arch-badge-glyph--check" />NO CHANGE</span>
        <div className="arch-tband-rows">
          <div className="arch-trow"><span className="arch-trow-t">Client tier</span><span className="arch-trow-s">Same <code>org.apache.kafka.clients.consumer.Consumer&lt;K,V&gt;</code> &mdash; minimal code changes.</span></div>
          <div className="arch-trow"><span className="arch-trow-t">Consumer logic</span><span className="arch-trow-s"><em>Filter code is removed</em> &mdash; your consumer ends up <strong>smaller</strong>, not larger. One topic-name change.</span></div>
        </div>
      </div>
      <div className="arch-tband arch-tband--trade">
        <span className="arch-badge arch-badge--trade"><span className="arch-badge-glyph arch-badge-glyph--warn" />WHAT WE TRADE</span>
        <div className="arch-tband-rows">
          <div className="arch-trow"><span className="arch-trow-t">Broker tier</span><span className="arch-trow-s">Wire-compatible swap &mdash; Pulsar + KoP today; native Apache Kafka on the roadmap.</span></div>
          <div className="arch-trow"><span className="arch-trow-t">Filter tier</span><span className="arch-trow-s">Managed compute you size &mdash; no bespoke filter app to build &amp; operate, but compute still runs.</span></div>
        </div>
      </div>
      <div className="arch-tband-win">The win isn&rsquo;t &ldquo;nothing runs.&rdquo; It&rsquo;s that <strong>there&rsquo;s no bespoke filtering application you build, deploy, and operate</strong> &mdash; you trade owned application code for managed, sized compute.</div>
    </div>
  );
}

function ScalingThreeInner() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setP((x) => (x + 1) % KSA_PHASES.length), 3200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="arch-zoom">
      <div className="arch-z-main">
        <div className="arch-scale3" style={{ gridTemplateColumns: '1.55fr 1fr' }}>
          <div className="arch-scol">
            <div className="arch-scol-h"><span className="arch-scol-k">Data-plane scale + autoscale &middot; Key_Shared &times; HPA</span><span className="arch-scol-t">One consumer per active filter &mdash; the tier scales itself</span></div>
            <div className="arch-scol-d"><KeySharedAutoscale phase={p} /></div>
            <p className="arch-scol-note" key={p}><span className="arch-step-n">{p + 1}/4</span> {KSA_PHASES[p].note} <strong>No repartition, no rebalance.</strong></p>
          </div>
          <div className="arch-scol">
            <div className="arch-scol-h"><span className="arch-scol-k">Trade-offs</span><span className="arch-scol-t">A cost we own &mdash; not magic</span></div>
            <div className="arch-scol-d" style={{ display: 'flex', alignItems: 'stretch' }}><TradeOffs /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ScalingThree() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <ScalingThreeInner key={activation} />
    </div>
  );
}

/* Re-export ArchLocStrip in case slides.tsx wants to use it inline. */
export { ArchLocStrip };

/* ════════════════════════════════════════════════════════════════════════
   SECTION · "Demo · Stock Watchlist" — slides 17/18/19
   (ported from the design-files prototype: demo1.jsx + demo2.jsx + glyphs.jsx)

   Three slides that introduce the live demo:
     17 · ThreeLayersSlide — producer/broker/consumer, two-alike + one-different
     18 · ScenarioSlide    — the demo in one line + the 5-component cast
     19 · WiringSlide      — animated end-to-end SVG hero (looping AAPL tile)

   Reuses the arch-* primitives above. Continuous motion on slide 19 uses
   the deck's existing arch-fd/arch-fc/arch-fl marching classes for the
   flow lines, plus one new CSS-keyframed class for the AAPL message tile.
   ════════════════════════════════════════════════════════════════════════ */

/* ── Small geometric icons for the cast cards (slide 18) ────────────────
   Geometric primitives only — no logos. Each is a 46×46 svg. */

function IcoProducer() {
  return (
    <svg viewBox="0 0 46 46" className="demo-castcard-ico" aria-hidden="true">
      <rect x="6" y="11" width="24" height="24" rx="6" fill="#E9E2DA" stroke="#364152" strokeWidth="2.2" />
      <circle cx="13" cy="18" r="2" fill="#364152" />
      <circle cx="13" cy="23" r="2" fill="#364152" />
      <circle cx="13" cy="28" r="2" fill="#364152" />
      {[16, 23, 30].map((y, i) => (
        <line key={i} x1="32" y1={y} x2="42" y2={y} stroke="#697586" strokeWidth="2" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function IcoBroker() {
  return (
    <svg viewBox="0 0 46 46" className="demo-castcard-ico" aria-hidden="true">
      <rect x="8" y="9" width="30" height="28" rx="6" fill="#D1E5FB" stroke="#09315F" strokeWidth="2.4" />
      <line x1="8" y1="20" x2="38" y2="20" stroke="#09315F" strokeWidth="1.8" />
      <circle cx="14" cy="14.5" r="1.8" fill="#09315F" />
      <circle cx="20" cy="14.5" r="1.8" fill="#09315F" />
      <rect x="14" y="26" width="18" height="5" rx="2.5" fill="#B9D7FA" />
    </svg>
  );
}

function IcoFilter() {
  return (
    <svg viewBox="0 0 46 46" className="demo-castcard-ico" aria-hidden="true">
      <path d="M9 11 L37 11 L27 25 L27 36 L19 36 L19 25 Z" fill="#EFD49C" stroke="#8A5210" strokeWidth="2.4" strokeLinejoin="round" />
      <ellipse cx="23" cy="11" rx="14" ry="2.8" fill="#E0B968" stroke="#8A5210" strokeWidth="2.2" />
    </svg>
  );
}

function IcoSdk() {
  return (
    <svg viewBox="0 0 46 46" className="demo-castcard-ico" aria-hidden="true">
      <rect x="20" y="10" width="20" height="26" rx="6" fill="#fff" stroke="#082B3D" strokeWidth="2.2" />
      <line x1="20" y1="18" x2="40" y2="18" stroke="#082B3D" strokeWidth="1.6" />
      <rect x="8" y="16" width="9" height="14" rx="3" fill="#C1CFD7" stroke="#082B3D" strokeWidth="2.2" />
    </svg>
  );
}

function IcoObs() {
  return (
    <svg viewBox="0 0 46 46" className="demo-castcard-ico" aria-hidden="true">
      <path d="M7 23 Q23 8 39 23 Q23 38 7 23 Z" fill="#F7E4BE" stroke="#B5781F" strokeWidth="2.2" strokeLinejoin="round" />
      <circle cx="23" cy="23" r="6.5" fill="#fff" stroke="#B5781F" strokeWidth="2.2" />
      <circle cx="23" cy="23" r="2.6" fill="#B5781F" />
    </svg>
  );
}

/* ── Larger 54×54 layer icons for slide 17 (Three Layers) ──────────────── */

function IcoProducerLg() {
  return (
    <svg viewBox="0 0 54 54" className="tl-ico" aria-hidden="true">
      <rect x="6" y="12" width="28" height="30" rx="7" fill="#E9E2DA" stroke="#364152" strokeWidth="2.6" />
      <line x1="6" y1="23" x2="34" y2="23" stroke="#364152" strokeWidth="1.8" />
      <circle cx="13" cy="17.5" r="2" fill="#364152" />
      <circle cx="20" cy="17.5" r="2" fill="#364152" />
      <rect x="12" y="29" width="16" height="6" rx="3" fill="#D7CDC3" />
      {[19, 27, 35].map((y, i) => (
        <line key={i} x1="37" y1={y} x2="48" y2={y} stroke="#697586" strokeWidth="2.4" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function IcoConsumerLg() {
  return (
    <svg viewBox="0 0 54 54" className="tl-ico" aria-hidden="true">
      {[19, 27, 35].map((y, i) => (
        <line key={i} x1="6" y1={y} x2="17" y2={y} stroke="#697586" strokeWidth="2.4" strokeLinecap="round" />
      ))}
      <rect x="20" y="12" width="28" height="30" rx="7" fill="#E9E2DA" stroke="#364152" strokeWidth="2.6" />
      <line x1="20" y1="23" x2="48" y2="23" stroke="#364152" strokeWidth="1.8" />
      <circle cx="27" cy="17.5" r="2" fill="#364152" />
      <circle cx="34" cy="17.5" r="2" fill="#364152" />
      <rect x="26" y="29" width="16" height="6" rx="3" fill="#D7CDC3" />
    </svg>
  );
}

function IcoFilterLg() {
  return (
    <svg viewBox="0 0 54 54" className="tl-ico" aria-hidden="true">
      <path d="M9 13 L45 13 L32 31 L32 43 L22 43 L22 31 Z" fill="#D1E5FB" stroke="#7AD8CB" strokeWidth="2.6" strokeLinejoin="round" />
      <ellipse cx="27" cy="13" rx="18" ry="3.4" fill="#B9D7FA" stroke="#7AD8CB" strokeWidth="2.4" />
    </svg>
  );
}

function VanillaBadge() {
  return (
    <span className="tl-badge tl-badge--vanilla">
      <svg viewBox="0 0 16 16" className="tl-badge-ico" aria-hidden="true">
        <circle cx="8" cy="8" r="7.2" fill="#1C8A5B" />
        <path d="M4.4 8.2 L6.9 10.6 L11.6 5.6" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Vanilla Kafka
    </span>
  );
}

/* ── Slide 17 · ThreeLayersSlide ───────────────────────────────────────── */

function ThreeLayersSlideInner() {
  return (
    <div className="demo-wrap demo-wrap--center">
      <div className="demo-layers">
        <div className="tl-band tl-band--vanilla">
          <IcoProducerLg />
          <div className="tl-tx">
            <span className="tl-layer">Layer 1 &middot; Producer</span>
            <span className="tl-nm">ShadowTraffic &rarr; stock-quotes</span>
            <span className="tl-ds">
              Standard Kafka producer. Records + <code>exchange</code> / <code>sector</code> /
              {' '}<code>side</code> headers. Zero Pulsar awareness, no SDK.
            </span>
          </div>
          <VanillaBadge />
        </div>

        <div className="tl-link">
          <span className="tl-link-cap">
            produces <b>~35k msg/s</b>
          </span>
          <span className="tl-link-line" />
        </div>

        <div className="tl-band tl-band--swap">
          <IcoFilterLg />
          <div className="tl-tx">
            <span className="tl-layer">Layer 2 &middot; Broker &mdash; the one swap</span>
            <span className="tl-nm">Pulsar + KoP &middot; Kafka-API-compatible</span>
            <span className="tl-ds">
              Speaks the Kafka wire protocol; hosts one filter function that deserializes
              once, evaluates every selector, and fans matches out.
            </span>
          </div>
          <span className="tl-badge tl-badge--swap">Broker-side detail</span>
        </div>

        <div className="tl-link">
          <span className="tl-link-cap">
            delivers <b>only matches</b>
          </span>
          <span className="tl-link-line" />
        </div>

        <div className="tl-band tl-band--vanilla">
          <IcoConsumerLg />
          <div className="tl-tx">
            <span className="tl-layer">Layer 3 &middot; Consumer</span>
            <span className="tl-nm">KafkaConsumer &middot; native poll()</span>
            <span className="tl-ds">
              Standard <code>poll()</code> loop, <code>StringDeserializer</code>, consumer
              groups. One thin SDK <code>.selector(&hellip;)</code> call returns a real
              {' '}<code>KafkaConsumer</code>.
            </span>
          </div>
          <VanillaBadge />
        </div>
      </div>

      <div className="demo-layers-foot">
        <span className="demo-layers-foot-k">
          <span className="arch-badge-glyph arch-badge-glyph--warn" />Honest
        </span>
        <span className="demo-layers-foot-b">
          It&rsquo;s a <strong>wire-compatible broker swap</strong>, not &ldquo;the same
          broker&rdquo; &mdash; and the filter is <strong>managed compute you size</strong>,
          not free. But the producer and consumer never leave the Kafka ecosystem.
        </span>
      </div>
    </div>
  );
}

export function ThreeLayersSlide() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <ThreeLayersSlideInner key={activation} />
    </div>
  );
}

/* ── Slide 18 · ScenarioSlide ──────────────────────────────────────────── */

type CastMod = 'kafka' | 'broker' | 'sdk' | 'obs';
type CastEntry = {
  mod: CastMod;
  Ico: () => React.ReactElement;
  nm: string;
  tag: string;
  ds: React.ReactNode;
};

const CAST: ReadonlyArray<CastEntry> = [
  {
    mod: 'kafka',
    Ico: IcoProducer,
    nm: 'ShadowTraffic',
    tag: 'Producer · native Kafka',
    ds: (
      <>
        Synthetic quotes through the <strong>standard Kafka producer</strong>.{' '}
        <code>exchange</code> / <code>sector</code> / <code>side</code> ride as headers.
      </>
    ),
  },
  {
    mod: 'broker',
    Ico: IcoBroker,
    nm: 'KoP broker (Pulsar)',
    tag: 'Broker',
    ds: (
      <>
        Speaks the Kafka wire protocol; hosts the filter compute. Maps headers &rarr;
        message properties.
      </>
    ),
  },
  {
    mod: 'broker',
    Ico: IcoFilter,
    nm: 'One filter function',
    tag: 'Broker-side',
    ds: (
      <>
        Spawned for <code>stock-quotes</code>. Holds all 9 selectors; deserializes each
        message once.
      </>
    ),
  },
  {
    mod: 'sdk',
    Ico: IcoSdk,
    nm: 'multi-query-app',
    tag: 'Kafka SDK',
    ds: (
      <>
        <strong>Plain Java</strong>, one app, two roles: registers the 9 selectors{' '}
        <em>and</em> drains 9 <code>KafkaConsumer</code>s.
      </>
    ),
  },
  {
    mod: 'obs',
    Ico: IcoObs,
    nm: 'Observability UI',
    tag: 'Observability',
    ds: (
      <>
        Looks <strong>inside</strong> the filter: live selectors, per-query drop %, rates,
        compile diagnostics.
      </>
    ),
  },
];

function ScenarioSlideInner() {
  return (
    <div className="demo-wrap">
      <div className="demo-scn">
        <div>
          <p className="demo-scn-lead">
            A synthetic <strong>stock-quote stream</strong> is filtered{' '}
            <span className="hl">at the broker</span> into nine consumer-specific slices
            &mdash; watchlists and market segments. Each consumer receives{' '}
            <strong>only its matching records</strong>: no client-side filtering, no Pulsar
            client libraries.
          </p>
          <div className="demo-metrics">
            <div className="demo-metric">
              <span className="demo-metric-n">9</span>
              <span className="demo-metric-l">consumer slices</span>
            </div>
            <div className="demo-metric">
              <span className="demo-metric-n teal">1</span>
              <span className="demo-metric-l">broker-side filter fn</span>
            </div>
            <div className="demo-metric">
              <span className="demo-metric-n zero">0</span>
              <span className="demo-metric-l">client-side filters</span>
            </div>
          </div>
        </div>
        <div className="demo-cast">
          <span className="demo-cast-k">The cast &middot; 5 components</span>
          {CAST.map((c) => (
            <div key={c.nm} className={`demo-castcard demo-castcard--${c.mod}`}>
              <c.Ico />
              <div className="demo-castcard-tx">
                <span className="demo-castcard-nm">{c.nm}</span>
                <span className="demo-castcard-ds">{c.ds}</span>
              </div>
              <span className={`demo-castcard-tag demo-tag--${c.mod}`}>{c.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ScenarioSlide() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <ScenarioSlideInner key={activation} />
    </div>
  );
}

/* ── Slide 19 · WiringSlide (animated hero) ────────────────────────────── */

const WIRE_QS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9'] as const;

/* Per-cycle selector hit subsets. Each entry is the list of selector
   indices (0-based) that "match" this cycle's message — i.e., the
   queries whose paths get a flying message tile from the funnel to
   their output topic. Designed to feel random across cycles: subset
   sizes vary (2-4), no single selector matches every cycle, and the
   chosen indices skip around vertically so the animation never looks
   like a metronome.  */
const WIRE_HITS: ReadonlyArray<ReadonlyArray<number>> = [
  [0, 3, 6],
  [1, 4, 7, 8],
  [0, 2, 5],
  [3, 4, 6, 8],
  [1, 5, 7],
  [0, 2, 4, 7],
  [3, 6, 8],
];

/* CSS for the new motion that the existing arch-* classes don't cover:
   the AAPL message tile travels from ShadowTraffic into the funnel mouth.
   Marching dashes on flow lines reuse arch-fd / arch-fc / arch-fl above. */
const WIRE_CSS = `
  @media (prefers-reduced-motion: no-preference) {
    .wire-aapl-anim { animation: wire-aapl-move 2.4s linear infinite; }
    @keyframes wire-aapl-move {
      0%   { transform: translate(196px, 292px); opacity: 0; }
      8%   { transform: translate(220px, 292px); opacity: 1; }
      50%  { transform: translate(372px, 292px); opacity: 1; }
      85%  { transform: translate(500px, 292px); opacity: 1; }
      100% { transform: translate(500px, 292px); opacity: 0; }
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .wire-aapl-anim { transform: translate(312px, 292px); opacity: 1; }
  }
`;

function WiringSlideInner() {
  const rowY = WIRE_QS.map((_, i) => 116 + i * 44);
  const funCx = 560;
  const funCy = rowY[4];
  const funR = 60;
  const selX = 690;
  const selW = 150;
  const outCx = 952;
  const appX = 1150;
  const D = ARCH_FLOW.data;
  const C = ARCH_FLOW.control;
  const A = '#B5781F';

  /* Cycle through WIRE_HITS every 3 s; on each tick a different subset
     of selectors gets a flying matched-message tile. Mirrors the slide-15
     ProcessorSlide pattern. */
  const [hitIdx, setHitIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHitIdx((x) => (x + 1) % WIRE_HITS.length), 3000);
    return () => clearInterval(id);
  }, []);
  const matchedNow = WIRE_HITS[hitIdx];
  const funnelExitX = funCx + funR - 6;
  return (
    <div className="demo-wire">
      <div className="demo-wire-svg">
        <svg
          data-arch="1"
          style={{ display: 'block' }}
          viewBox="0 0 1400 600"
          role="img"
          aria-label="End-to-end wiring. ShadowTraffic produces through a native Kafka producer to the stock-quotes topic, with exchange/sector/side as headers. Inside the Kafka-API-compatible broker (Pulsar plus KoP), one filter function deserializes each message once, evaluates it against nine selectors, and fans matches out to nine private output topics. The multi-query-app, using the Kafka SDK, registers the nine selectors and drains nine native KafkaConsumers from those topics. An observability UI reads the filter's internals."
        >
          <style>{WIRE_CSS}</style>

          {/* ShadowTraffic producer (native Kafka) */}
          <rect x={36} y={244} width={150} height={96} rx={12} fill="#fff" stroke={ARCH_PAL.slate.stroke} strokeWidth={2.4} />
          <rect x={36} y={244} width={150} height={22} rx={12} fill={ARCH_PAL.slate.fill} />
          <rect x={36} y={256} width={150} height={10} fill={ARCH_PAL.slate.fill} />
          <line x1={36} y1={266} x2={186} y2={266} stroke={ARCH_PAL.slate.stroke} strokeWidth={1.4} />
          {[0, 1, 2].map((i) => (
            <circle key={i} cx={49 + i * 11} cy={255} r={2.5} fill={ARCH_PAL.slate.stroke} opacity={0.55} />
          ))}
          <text x={111} y={296} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={16} fontWeight={700} fill={ARCH_PAL.slate.ink}>ShadowTraffic</text>
          <text x={111} y={316} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9} fill="#697586" letterSpacing={0.6}>NATIVE KAFKA PRODUCER</text>
          <text x={111} y={362} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fontWeight={700} fill={ARCH_PAL.slate.ink} letterSpacing={0.4}>~43 symbols &middot; headers</text>

          {/* producer -> stock-quotes */}
          <line x1={186} y1={292} x2={246} y2={292} stroke={D} strokeWidth={3.2} className="arch-fd" strokeLinecap="round" />
          <ArchHead x={246} y={292} ang={0} color={D} size={9} />

          {/* stock-quotes topic */}
          <ArchCylinder cx={312} top={236} w={120} h={112} tone="teal" label="stock-quotes" strong />

          {/* stock-quotes -> broker */}
          <line x1={372} y1={292} x2={432} y2={292} stroke={D} strokeWidth={3.2} className="arch-fd" strokeLinecap="round" />
          <ArchHead x={432} y={292} ang={0} color={D} size={9} />

          {/* BROKER box (shifted up 15px — y=84→69, then right 15px —
             x=440→455. Contents inside stay put.) */}
          <rect x={455} y={69} width={560} height={432} rx={16} fill="#F7FAFE" stroke="#0E4A8E" strokeWidth={2.2} />
          <text x={475} y={55} fontFamily="DM Mono, monospace" fontSize={12.5} fontWeight={700} fill="#09315F" letterSpacing={0.6}>KoP broker &middot; Kafka-API-compatible (Pulsar + KoP)</text>

          {/* the filter funnel (1 function) */}
          <ArchFunnel cx={funCx} cy={funCy} r={funR} spin />
          <text x={funCx} y={funCy + funR + 26} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#09315F" letterSpacing={0.4}>1 filter function</text>
          <text x={funCx} y={funCy + funR + 42} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fill="#0E4A8E" letterSpacing={0.4}>deserialize once</text>

          {/* 9 selector cards + fan-out to 9 private output topics */}
          <text x={selX + selW / 2} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={700} fill="#09315F" letterSpacing={1}>9 SELECTORS &middot; EVALUATE EACH MSG</text>
          <text x={outCx} y={89} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fontWeight={700} fill="#082B3D" letterSpacing={0.6}>9 OUTPUT TOPICS</text>
          {WIRE_QS.map((q, i) => {
            const y = rowY[i];
            return (
              <g key={q}>
                {/* funnel -> selector */}
                <line x1={funCx + funR - 6} y1={funCy} x2={selX - 6} y2={y} stroke={C} strokeWidth={1.5} className="arch-fc" opacity={0.7} />
                {/* selector card */}
                <rect x={selX} y={y - 14} width={selW} height={28} rx={6} fill="#fff" stroke="#B9D7FA" strokeWidth={1.6} />
                <text x={selX + 12} y={y + 5} fontFamily="DM Mono, monospace" fontSize={12} fontWeight={700} fill="#09315F">{q}</text>
                <text x={selX + selW - 12} y={y + 5} textAnchor="end" fontFamily="DM Mono, monospace" fontSize={9} fill="#697586">selector</text>
                {/* selector -> output topic */}
                <line x1={selX + selW} y1={y} x2={outCx - 18} y2={y} stroke={D} strokeWidth={2.4} className="arch-fd" strokeLinecap="round" />
                {/* output topic tick (mini cylinder) */}
                <ellipse cx={outCx} cy={y - 11} rx={18} ry={4} fill={ARCH_PAL.teal.top} stroke={ARCH_PAL.teal.stroke} strokeWidth={1.6} />
                <path d={`M${outCx - 18},${y - 11} V${y + 11} A18,4 0 0 0 ${outCx + 18},${y + 11} V${y - 11}`} fill={ARCH_PAL.teal.fill} stroke={ARCH_PAL.teal.stroke} strokeWidth={1.6} />
              </g>
            );
          })}

          {/* Matched-message tiles — one per selector in the current
             WIRE_HITS subset. Each travels funnel exit → selector card →
             output topic via SMIL animateMotion (same pattern as slide-15
             FFMsg). The wrapper <g key={hitIdx}> remounts the whole subtree
             when the cycle ticks, which restarts the SMIL animations from
             t=0 with the new subset. We use repeatCount="indefinite" +
             dur="3s" (matched to the cycle interval) so the animation
             loops continuously inside the cycle — same idiom slide 15
             uses; tried one-shot + fill="freeze" first and SMIL's begin-
             relative-to-doc-load semantics killed it after cycle 0. */}
          <g key={hitIdx}>
            {matchedNow.map((i) => {
              const y = rowY[i];
              const path = `M${funnelExitX},${funCy} L${selX + 12},${y} L${selX + selW - 12},${y} L${outCx - 18},${y}`;
              return (
                <g key={`wmsg-${i}`} opacity="0">
                  <rect x={-14} y={-9} width={28} height={18} rx={4} fill="#0070D9" stroke="#0C5FB7" strokeWidth={1.3} />
                  <text y={4} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9} fontWeight={700} fill="#fff">msg</text>
                  <animateMotion
                    dur="3s"
                    repeatCount="indefinite"
                    calcMode="linear"
                    path={path}
                    keyTimes="0;0.32;0.5;0.9;1"
                    keyPoints="0;0.32;0.5;1;1"
                  />
                  <animate
                    attributeName="opacity"
                    dur="3s"
                    repeatCount="indefinite"
                    values="0;1;1;1;0"
                    keyTimes="0;0.08;0.5;0.85;1"
                  />
                </g>
              );
            })}
          </g>

          {/* travelling AAPL message into the funnel (CSS keyframed) */}
          <g className="wire-aapl-anim" style={{ transformBox: 'fill-box', transformOrigin: 'left top' }}>
            <rect x={-15} y={-10} width={30} height={20} rx={4} fill="#0070D9" stroke="#0C5FB7" strokeWidth={1.3} />
            <text y={4} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fontWeight={700} fill="#fff">AAPL</text>
          </g>

          {/* multi-query-app (right) — height sized to hug the last line
             of text ("register · consume" at y=360); rect bottom at y=375
             leaves a tight 12-px pad below the baseline without any
             trailing blank space. */}
          <rect x={appX} y={150} width={214} height={225} rx={14} fill="#fff" stroke={ARCH_PAL.slate.stroke} strokeWidth={2.4} />
          <rect x={appX} y={150} width={214} height={26} rx={14} fill={ARCH_PAL.slate.fill} />
          <rect x={appX} y={163} width={214} height={13} fill={ARCH_PAL.slate.fill} />
          <line x1={appX} y1={176} x2={appX + 214} y2={176} stroke={ARCH_PAL.slate.stroke} strokeWidth={1.4} />
          <text x={appX + 107} y={168} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10} fontWeight={700} fill={ARCH_PAL.slate.ink} letterSpacing={0.6}>multi-query-app &middot; plain Java</text>
          {/* Kafka SDK shim */}
          <rect x={appX + 22} y={196} width={170} height={40} rx={8} fill={ARCH_PAL.teal.fill} stroke={ARCH_PAL.teal.stroke} strokeWidth={2} />
          <text x={appX + 107} y={214} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={14} fontWeight={700} fill={ARCH_PAL.teal.ink}>Kafka SDK</text>
          <text x={appX + 107} y={228} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={8.5} fill="#082B3D" letterSpacing={0.4}>registers + returns consumers</text>
          {/* 9 KafkaConsumers */}
          <rect x={appX + 22} y={250} width={170} height={66} rx={8} fill="#F4F1EC" stroke={ARCH_PAL.slate.stroke} strokeWidth={1.8} />
          <text x={appX + 107} y={278} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={15} fontWeight={700} fill={ARCH_PAL.slate.ink}>9 &times; KafkaConsumer</text>
          <text x={appX + 107} y={298} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9} fill="#697586" letterSpacing={0.4}>native poll() &middot; one per query</text>
          <text x={appX + 107} y={344} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={9.5} fontWeight={700} fill={ARCH_PAL.slate.ink} letterSpacing={0.4}>ONE APP &middot; TWO ROLES</text>
          <text x={appX + 107} y={360} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={8.5} fill="#697586" letterSpacing={0.4}>register &middot; consume</text>

          {/* output topics -> app (drain lines) */}
          {WIRE_QS.map((q, i) => {
            const y = rowY[i];
            return (
              <line key={q} x1={outCx + 18} y1={y} x2={appX} y2={283} stroke={D} strokeWidth={1.5} className="arch-fd" opacity={0.55} strokeLinecap="round" />
            );
          })}
          <ArchHead x={appX} y={283} ang={archAng(outCx, 300, appX, 283)} color={D} size={8} />

          {/* registration arc: app SDK -> funnel (over the top) */}
          <path d="M1180 196 C 1120 110 760 96 580 232" fill="none" stroke={C} strokeWidth={2} className="arch-fc" />
          <ArchHead x={580} y={232} ang={archAng(620, 150, 580, 232)} color={C} size={8} />
          <text x={1130} y={120} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={10.5} fontWeight={700} fill="#09315F" letterSpacing={0.4}>registers 9 selectors</text>

          {/* observability UI -> filter internals
             Positioned BELOW the multi-query-app box (app bottom is y=470)
             with its left edge aligned to the app's (both at x=1150) and
             matching its width (214). The dashed amber line then crosses
             the broker boundary on its way to the funnel — reads as
             "external UI reaches into the broker's filter to read its
             internals." */}
          <rect x={appX} y={399} width={214} height={54} rx={10} fill="#fff" stroke={A} strokeWidth={2} />
          <text x={appX + 107} y={421} textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontSize={13} fontWeight={700} fill={A}>Observability UI</text>
          <text x={appX + 107} y={439} textAnchor="middle" fontFamily="DM Mono, monospace" fontSize={8.5} fill="#8A5210" letterSpacing={0.4}>reads the filter&rsquo;s internals</text>
          {/* Dashed amber line: obs box LEFT-EDGE middle (1150, 426) →
             broker's nearest edge (right edge at x=1000, same y). Pure
             horizontal straight line; arrowhead points west into the
             broker. */}
          <path d={`M${appX} 426 L1000 426`} fill="none" stroke={A} strokeWidth={1.8} className="arch-fl" />
          <ArchHead x={1000} y={426} ang={180} color={A} size={7} />
        </svg>
      </div>

      <div className="demo-wire-legend">
        <span className="demo-leg">
          <span className="demo-leg-sw in" />
          <strong>Native Kafka</strong> &mdash; produce / consume
        </span>
        <span className="demo-leg">
          <span className="demo-leg-sw ctrl" />
          <strong>SDK control</strong> &mdash; register selectors
        </span>
        <span className="demo-leg">
          <span className="demo-leg-sw match" />
          <strong>Filtered data</strong> &mdash; matches fan out
        </span>
      </div>
    </div>
  );
}

export function WiringSlide() {
  const [ref, activation] = useSlideActivation();
  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <WiringSlideInner key={activation} />
    </div>
  );
}
